/**
 * Raid Leader battle rules — Phaser-free.
 * Commander (player) issues orders; adventurers + boss tick via AI.
 */
import {
  raidActiveFor,
  raidCatalog,
  raidHeroProfileFor,
  type RaidClassId,
} from "../../data/raid-catalog";
import {
  activeOrder,
  applySoftSepSpatial,
  clampPointToRect,
  createBehaviorState,
  createHitSignal,
  createOrderSlot,
  insetRect,
  issueOrder,
  noteHit,
  SpatialHash2D,
  separateCircles,
  separateFromFixed,
  shouldKeepClosingOnReach,
  planDashAroundHardDisk,
  wallAwareSepImpulse,
  zeroOutwardVelocity,
} from "../../sim";
import { computeAiIntent, pointInTelegraph } from "./raid-ai";
import {
  clearUnitOrder,
  invalidateUnitSelect,
  hostileHardMinSep,
  insightAxisOf,
  resolveDisposition,
} from "./raid-brain";
import { flushRaidDeferredSkillHits } from "./raid-deferred-hits";
import { plantBlendFromMeasure, SWING_STOP_PLANT_BLEND } from "./raid-kit-interp";
import { raidDangerColor, clearOfPackChewAttack } from "./raid-threat";
import {
  RaidEnemyDefId,
  RaidEnemyTag,
  enemyHasTag,
} from "./raid-enemy";
import { getActivePlayBounds } from "./sim-lab/sim-stage";
import {
  muteInwardLeapSepImpulse,
  skillZoneSoftSepInwardMute,
} from "./raid-skill-zone-policy";
import {
  bossThreatHudRanks,
  createRaidAggro,
  decayRaidAggro,
  noteDamageThreat,
  noteFocusTaunt,
  noteMarkedShotThreat,
  notePeelPulse,
  noteShieldBashTaunt,
  noteToughnessThreat,
  pickBossAggroTarget,
  pickLeapAggroTarget,
  RAID_AGGRO_BOSS_ID,
  resolveBossAggroHudTarget,
  seedTankAggroBias,
} from "./raid-aggro";
import {
  addWaveBarkChance,
  addWaveBarkMinCount,
  bigHitBarkChance,
  bigHitBarkCooldownSec,
  bigHitDamageThreshold,
  bossBarkChance,
  bossBarkCooldownSec,
  pickAddWaveBarkKey,
  pickAddWaveSpeaker,
  pickBigHitBarkKey,
  pickBossBarkKey,
  pickDeathReactBarkKey,
  pickDeathReactSpeaker,
  pickGroupDashSpeaker,
  pickOrderBarkKey,
  rollBarkGate,
  type RaidBarkOrderId,
  type RaidBossBarkMoment,
} from "./raid-bark";
import { RaidEvent, type RaidSimEvent } from "./raid-events";
import { createSpawnHeat, noteEnemySpawn } from "./raid-spawn-heat";
import { raidCosmeticRandom, raidRandom } from "./raid-rng";
import {
  assaultScript,
  isBossDormant,
  tickAssault,
  type AssaultSpawnFns,
} from "./raid-assault";
import {
  getEscalateStacks,
  scaleEnemyOutgoingDamage,
} from "./raid-escalate";
import {
  damageBrute,
  isRaidBrute,
  livingArmedBrutes,
  tickBruteSkills,
  triggerBruteBreak,
} from "./raid-brute-skills";
import {
  damageCharger,
  isRaidCharger,
  tickChargerSkills,
} from "./raid-charger-skills";
import { raidFreezeDiag, raidFreezeNowMs } from "./raid-freeze-diag";
import {
  bruteVariantNameKey,
  bruteWaveRankById,
} from "./raid-brute-variant";
import type {
  RaidBattleState,
  RaidBoss,
  RaidCommander,
  RaidFormationAnchor,
  RaidKnockFlight,
  RaidSituationId,
  RaidUnit,
} from "./raid-types";
import { RaidSituation } from "./raid-types";
import { easeInOutCubic, sampleKnockFlight } from "../../vfx/motion-curves";

export { startAssaultEncounter, seekAssaultWave, isBossDormant, isAssaultMode, isBossEncounterActive } from "./raid-assault";
export {
  tryEmitAssaultChunkBark,
  tryEmitAddCombatBark,
  tryEmitBruteBark,
  tryEmitBrutePackSpawnBark,
} from "./raid-bark-emit";
export {
  damageBrute,
  isRaidBrute,
  livingArmedBrutes,
  applyBruteToughnessFromHit,
  triggerBruteBreak,
} from "./raid-brute-skills";
export {
  damageCharger,
  isRaidCharger,
  livingChargers,
  tickChargerSkills,
} from "./raid-charger-skills";
export {
  bruteVariantLook,
  bruteVariantLookForUnit,
  bruteVariantNameKey,
  bruteWaveRankById,
} from "./raid-brute-variant";

export type {
  RaidBattleState,
  RaidBoss,
  RaidCommander,
  RaidFormationAnchor,
  RaidKnockFlight,
  RaidPhase,
  RaidSituationId,
  RaidSituationSlot,
  RaidUnit,
} from "./raid-types";
export { RaidSituation } from "./raid-types";
export { RaidEvent } from "./raid-events";
export type { RaidSimEvent } from "./raid-events";

const sepHash = new SpatialHash2D<RaidUnit>();
const sepNear: RaidUnit[] = [];

/** Enemy telegraphs only — never commander orders/gestures. */
const SITUATION_PRIORITY: readonly RaidSituationId[] = [
  RaidSituation.BossBreak,
  RaidSituation.BossCastBrass,
];

function setSituation(
  state: RaidBattleState,
  id: RaidSituationId,
  messageKey: string,
  until?: number,
): void {
  state.situations[id] = until != null ? { messageKey, until } : { messageKey };
}

function clearSituation(state: RaidBattleState, id: RaidSituationId): void {
  if (state.situations[id] != null) delete state.situations[id];
}

/** Commander commit messageKey + echo TTL for prep-chip linger (not enemy toast). */
function noteCommanderFeedback(state: RaidBattleState, messageKey: string): void {
  state.commander.lastActionKey = messageKey;
  const echoSec = Number(gestureTable().gesture?.commitEchoSec);
  state.commander.lastActionUntil =
    state.time + (Number.isFinite(echoSec) && echoSec > 0 ? echoSec : 2);
}

/** Drop expired / invalid slots; returns highest-priority messageKey still active. */
function syncSituations(state: RaidBattleState): string | null {
  const boss = state.boss;
  const broken = state.time < boss.stunnedUntil;
  if (!broken) clearSituation(state, RaidSituation.BossBreak);

  const brassActive =
    boss.telegraph != null && boss.telegraph.skillId === "brass";
  if (!brassActive) clearSituation(state, RaidSituation.BossCastBrass);

  for (const id of SITUATION_PRIORITY) {
    const slot = state.situations[id];
    if (slot) return slot.messageKey;
  }
  return null;
}

function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(ax - bx, ay - by);
}

/** hit-signal attacker sentinel for boss-sourced damage (unit ids are >= 1). */
export const BOSS_ATTACKER_ID = -2;

function freshBrain() {
  return {
    select: createBehaviorState(),
    targetId: -1,
    nextDecideAt: 0,
    order: createOrderSlot<"dash" | "pin">(),
    gestureAnchor: null as RaidFormationAnchor | null,
    dangerReactAt: 0,
    engageRangeIds: [] as number[],
    softSpaceLatch: false,
    thrallEscapeSeat: null as { x: number; y: number } | null,
    thrallEscapeAa: 0,
  };
}

/** Presentation hold timings (seconds) — SSOT in raid-view.json fx. */
export function raidDeathFx(): {
  unitDeathSec: number;
  bossDeathSec: number;
  loseHoldSec: number;
} {
  const fx = (
    raidCatalog.view as {
      fx?: {
        unitDeathSec?: number;
        bossDeathSec?: number;
        loseHoldSec?: number;
      };
    }
  ).fx;
  return {
    unitDeathSec: Math.max(0.2, Number(fx?.unitDeathSec) || 1),
    bossDeathSec: Math.max(0.4, Number(fx?.bossDeathSec) || 1.6),
    loseHoldSec: Math.max(0.2, Number(fx?.loseHoldSec) || 0.85),
  };
}

/** Drop active gesture dash (interrupt / death). Pin is cleared only by release or replace. */
function clearDashOrder(u: RaidUnit): void {
  if (u.brain.order.current?.kind === "dash") {
    clearUnitOrder(u);
  }
}

/**
 * Clear combat intent and emit Death once when HP first hits 0.
 * `keepKnock`: lethal leap/slam/brass/charge — corpse finishes flight before
 * death FX (presentation holds beginUnitDeath while knock is active).
 */
function finalizeUnitDeath(
  state: RaidBattleState,
  u: RaidUnit,
  opts?: { keepKnock?: boolean },
): void {
  u.hp = 0;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  u.attackAnimT = 0;
  u.attackCdLeft = 1e9;
  if (!opts?.keepKnock) u.knock = null;
  clearDashOrder(u);
  push(state, { kind: RaidEvent.Death, unitId: u.id, team: u.team });
  if (u.team === 0) {
    tryEmitAllyDeathReact(state, u);
  }
}

function poolClass(defId: string): RaidClassId {
  const e = raidCatalog.roster.pool.find((p) => p.id === defId);
  return (e?.classId as RaidClassId) ?? "dps";
}

/** Presentation swing length from raid-view rig table (floor = attack clip). */
export function swingSecForRole(role: string): number {
  const byRole = raidCatalog.view.rig.byRole as Record<
    string,
    { swingSec?: number }
  >;
  const base =
    byRole[role]?.swingSec ?? raidCatalog.view.rig.defaults.swingSec ?? 0.36;
  const floor = raidCatalog.balance.combat.attackAnimFloorSec ?? 0.55;
  return Math.max(base, floor);
}

export function swingSecForClass(classId: RaidClassId): number {
  const map = { tank: "raid_tank", dps: "raid_dps", healer: "raid_healer" } as const;
  return swingSecForRole(map[classId]);
}

export function swingSecForBoss(): number {
  return swingSecForRole("raid_boss");
}

type PlayBounds = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

function playBounds(): PlayBounds {
  return getActivePlayBounds();
}

export function clampToPlayBounds(
  x: number,
  y: number,
  pad = 0,
): { x: number; y: number } {
  // Engine center-anchored clamp: oversized pad (boss radius) never inverts.
  return clampPointToRect(x, y, playBounds(), pad);
}

/** Radius-padded wall rect for a unit (engine helpers consume plain rects). */
function unitWallRect(radius: number) {
  return insetRect(playBounds(), radius);
}

function playBoundsCenter(): { x: number; y: number } {
  const b = playBounds();
  return { x: (b.xMin + b.xMax) * 0.5, y: (b.yMin + b.yMax) * 0.5 };
}

function arenaCenterDirFrom(
  fromX: number,
  fromY: number,
): { nx: number; ny: number } {
  const c = playBoundsCenter();
  let dx = c.x - fromX;
  let dy = c.y - fromY;
  const len = Math.hypot(dx, dy);
  if (len < 1e-3) return { nx: 0, ny: -1 };
  return { nx: dx / len, ny: dy / len };
}

type EdgeRecenterCfg = {
  mode: "leap" | "steer";
  edgeMargin: number;
  centerFarDist: number;
  dwellSec: number;
  cooldownSec: number;
  centerArriveDist: number;
  steerSec: number;
  graceSec: number;
};

function edgeRecenterCfg(): EdgeRecenterCfg | null {
  const raw = (
    demoBossDef() as { edgeRecenter?: Partial<EdgeRecenterCfg> }
  ).edgeRecenter;
  if (!raw) return null;
  return {
    mode: raw.mode === "steer" ? "steer" : "leap",
    edgeMargin: raw.edgeMargin ?? 36,
    centerFarDist: raw.centerFarDist ?? 300,
    dwellSec: raw.dwellSec ?? 4.5,
    cooldownSec: raw.cooldownSec ?? 16,
    centerArriveDist: raw.centerArriveDist ?? 90,
    steerSec: raw.steerSec ?? 3.5,
    graceSec: raw.graceSec ?? 8,
  };
}

/** Distance from boss root to nearest radius-inset playBounds edge. */
function bossDistToInsetEdge(boss: RaidBoss): number {
  const wall = unitWallRect(boss.radius);
  return Math.min(
    boss.x - wall.xMin,
    wall.xMax - boss.x,
    boss.y - wall.yMin,
    wall.yMax - boss.y,
  );
}

function updateBossEdgeDwell(state: RaidBattleState, dt: number): void {
  const cfg = edgeRecenterCfg();
  const boss = state.boss;
  if (!cfg) {
    boss.edgeDwellSec = 0;
    return;
  }
  if (state.time < cfg.graceSec) {
    boss.edgeDwellSec = 0;
    return;
  }
  const center = playBoundsCenter();
  const nearEdge = bossDistToInsetEdge(boss) <= cfg.edgeMargin;
  const farCenter =
    dist(boss.x, boss.y, center.x, center.y) >= cfg.centerFarDist;
  if (nearEdge || farCenter) {
    boss.edgeDwellSec += dt;
  } else {
    boss.edgeDwellSec = 0;
  }
}

function beginBossLeap(
  state: RaidBattleState,
  landX: number,
  landY: number,
  purpose: "combat" | "recenter" | "charge",
): void {
  const boss = state.boss;
  const leapDef = demoBossDef().leap;
  const land = clampLandPoint(landX, landY);
  boss.leap = {
    phase: "windup",
    remain: leapDef.windupSec,
    duration: leapDef.windupSec,
    landX: land.x,
    landY: land.y,
    fromX: boss.x,
    fromY: boss.y,
    landRadius: leapDef.landRadius,
    landDamage: leapDef.landDamage,
    danger: raidDangerColor(leapDef.danger),
    purpose,
  };
  boss.intentVx = 0;
  boss.intentVy = 0;
  boss.vx = 0;
  boss.vy = 0;
  push(state, {
    kind: RaidEvent.LeapTelegraph,
    x: land.x,
    y: land.y,
    radius: leapDef.landRadius,
    remain: leapDef.windupSec,
    duration: leapDef.windupSec,
    danger: raidDangerColor(leapDef.danger),
  });
  // Once per cast open (windup begin) — leap-specific lines, chance+cd gate.
  tryEmitBossBark(state, "leap");
}

/**
 * Edge-camp escape: prefer red leap to playBounds center (same dodge pipeline),
 * else soft-steer toward center for steerSec. Returns true if tick is consumed.
 */
function tryBossEdgeRecenter(state: RaidBattleState, dt: number): boolean {
  const cfg = edgeRecenterCfg();
  const boss = state.boss;
  if (!cfg) return false;

  // Active soft-steer window — walk to center, skip leap/slam/brass.
  if (boss.recenterSteerRemain > 0) {
    boss.recenterSteerRemain = Math.max(0, boss.recenterSteerRemain - dt);
    const center = playBoundsCenter();
    const d = dist(boss.x, boss.y, center.x, center.y);
    if (d <= cfg.centerArriveDist || boss.recenterSteerRemain <= 1e-6) {
      boss.recenterSteerRemain = 0;
      boss.edgeDwellSec = 0;
      boss.intentVx = 0;
      boss.intentVy = 0;
      boss.vx = 0;
      boss.vy = 0;
      return false;
    }
    const dx = center.x - boss.x;
    const dy = center.y - boss.y;
    const len = Math.hypot(dx, dy) || 1;
    boss.intentVx = (dx / len) * boss.speed;
    boss.intentVy = (dy / len) * boss.speed;
    boss.vx = boss.intentVx;
    boss.vy = boss.intentVy;
    boss.x += boss.vx * dt;
    boss.y += boss.vy * dt;
    clampBossToMap(boss);
    return true;
  }

  if (boss.edgeRecenterCdLeft > 0) return false;
  if (boss.edgeDwellSec < cfg.dwellSec) return false;

  boss.edgeDwellSec = 0;
  boss.edgeRecenterCdLeft = cfg.cooldownSec;

  if (cfg.mode === "steer") {
    boss.recenterSteerRemain = cfg.steerSec;
    return tryBossEdgeRecenter(state, dt);
  }

  const center = playBoundsCenter();
  beginBossLeap(state, center.x, center.y, "recenter");
  return true;
}

/**
 * Knock ground direction: primary = attack origin → victim.
 * Optional aimBlend mixes in telegraph aim (cone/brass blast axis).
 */
function resolveKnockDir(
  victimX: number,
  victimY: number,
  originX: number,
  originY: number,
  opts?: { aimAngle?: number; aimBlend?: number },
): { nx: number; ny: number } {
  let dx = victimX - originX;
  let dy = victimY - originY;
  let len = Math.hypot(dx, dy);
  let nx: number;
  let ny: number;
  if (len < 1e-3) {
    if (opts?.aimAngle != null) {
      nx = Math.cos(opts.aimAngle);
      ny = Math.sin(opts.aimAngle);
    } else {
      ({ nx, ny } = arenaCenterDirFrom(originX, originY));
    }
  } else {
    nx = dx / len;
    ny = dy / len;
  }

  const blend = opts?.aimBlend ?? 0;
  if (blend > 0 && opts?.aimAngle != null) {
    const ax = Math.cos(opts.aimAngle);
    const ay = Math.sin(opts.aimAngle);
    const w = Math.min(1, Math.max(0, blend));
    nx = nx * (1 - w) + ax * w;
    ny = ny * (1 - w) + ay * w;
    const nlen = Math.hypot(nx, ny) || 1;
    nx /= nlen;
    ny /= nlen;
  }

  // Edge: if outward step is blocked, keep attack→victim axis — slide along
  // the free axis before falling back to arena center.
  const trial = clampToPlayBounds(
    victimX + nx * 48,
    victimY + ny * 48,
    0,
  );
  if (Math.hypot(trial.x - victimX, trial.y - victimY) < 10) {
    const slideX = clampToPlayBounds(victimX + nx * 48, victimY, 0);
    const slideY = clampToPlayBounds(victimX, victimY + ny * 48, 0);
    const sx = slideX.x - victimX;
    const sy = slideY.y - victimY;
    if (Math.hypot(sx, sy) >= 8) {
      const sl = Math.hypot(sx, sy) || 1;
      return { nx: sx / sl, ny: sy / sl };
    }
    return arenaCenterDirFrom(originX, originY);
  }
  return { nx, ny };
}

function beginKnockFlight(
  u: RaidUnit,
  fromX: number,
  fromY: number,
  dist: number,
  dur: number,
  apex: number,
  opts?: { aimAngle?: number; aimBlend?: number; recoverSec?: number },
): void {
  const { nx, ny } = resolveKnockDir(u.x, u.y, fromX, fromY, opts);
  const start = clampToPlayBounds(u.x, u.y, u.radius);
  let end = clampToPlayBounds(
    start.x + nx * dist,
    start.y + ny * dist,
    u.radius,
  );
  // If end barely moved (corner), retry with axis slide then arena bias.
  if (Math.hypot(end.x - start.x, end.y - start.y) < dist * 0.25) {
    const slid = resolveKnockDir(start.x, start.y, fromX, fromY, opts);
    end = clampToPlayBounds(
      start.x + slid.nx * dist,
      start.y + slid.ny * dist,
      u.radius,
    );
  }
  u.x = start.x;
  u.y = start.y;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  clearDashOrder(u);
  u.knock = {
    fromX: start.x,
    fromY: start.y,
    toX: end.x,
    toY: end.y,
    apex: Math.max(0, apex),
    dur: Math.max(0.05, dur),
    t: 0,
    recoverSec: Math.max(0, opts?.recoverSec ?? 0),
  };
}

function bodySepPad(): number {
  const pad = raidCatalog.balance.combat.bodySepPad;
  return typeof pad === "number" ? pad : 8;
}

/**
 * Dash/groupDash goals inside a hostile hard-body disk are unreachable —
 * body-sep resets the unit to minSep every tick. Project onto the ring via
 * shared `planDashAroundHardDisk` (works for any radius hostile, not boss-only).
 */
function planDashAroundHostile(
  u: Pick<RaidUnit, "x" | "y" | "radius" | "attackRange">,
  tx: number,
  ty: number,
  hostile: { x: number; y: number; radius: number },
): { legX: number; legY: number; finalX: number; finalY: number } {
  const minSep = hostileHardMinSep(u, hostile);
  return planDashAroundHardDisk(u.x, u.y, tx, ty, hostile.x, hostile.y, minSep, {
    clamp: (x, y) => clampToPlayBounds(x, y, u.radius),
  });
}

/** Leap land: clear boss body — min separation as flight start, then knock dist. */
function beginLeapKnockFlight(
  u: RaidUnit,
  boss: RaidBoss,
  dist: number,
  dur: number,
  apex: number,
  recoverSec = 0,
): void {
  const minSep = boss.radius + u.radius + bodySepPad();
  const { nx, ny } = resolveKnockDir(u.x, u.y, boss.x, boss.y);
  const start = clampToPlayBounds(
    boss.x + nx * minSep,
    boss.y + ny * minSep,
    u.radius,
  );
  const end = clampToPlayBounds(
    boss.x + nx * (minSep + dist),
    boss.y + ny * (minSep + dist),
    u.radius,
  );
  u.x = start.x;
  u.y = start.y;
  u.vx = 0;
  u.vy = 0;
  u.intentVx = 0;
  u.intentVy = 0;
  clearDashOrder(u);
  u.knock = {
    fromX: start.x,
    fromY: start.y,
    toX: end.x,
    toY: end.y,
    apex: Math.max(0, apex),
    dur: Math.max(0.05, dur),
    t: 0,
    recoverSec: Math.max(0, recoverSec),
  };
}

function makeAlly(
  state: RaidBattleState,
  defId: string,
  x: number,
  y: number,
  mul?: { hp?: number; damage?: number },
): RaidUnit {
  const classId = poolClass(defId);
  const c = raidCatalog.balance.classes[classId];
  const active = raidActiveFor(classId);
  const id = state.nextId++;
  return {
    id,
    defId,
    classId,
    team: 0,
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    hp: c.hp * (mul?.hp ?? 1),
    maxHp: c.hp * (mul?.hp ?? 1),
    damage: c.damage * (mul?.damage ?? 1),
    radius: c.radius,
    speed: c.speed,
    attackRange: c.attackRange,
    attackCd: c.attackCd,
    attackCdLeft: raidRandom(state) * 0.4,
    skillId: active?.id ?? null,
    skillCdLeft: active ? raidRandom(state) * active.cooldown * 0.35 : 0,
    skillCdMax: active?.cooldown ?? 0,
    attackAnimT: 0,
    stunnedUntil: 0,
    knock: null,
    aimX: x,
    aimY: y - 1,
    brain: freshBrain(),
    hit: createHitSignal(),
    barkCdLeft: 0,
    aggro: createRaidAggro(),
    telegraph: null,
    leap: null,
    brassCd: 0,
    leapCd: 0,
    chargeCd: 0,
    toughness: 0,
    toughnessMax: 0,
    breakStunDuration: 0,
    combatArmed: true,
    dropRemain: 0,
  };
}

function makeEnemyAdd(
  state: RaidBattleState,
  x: number,
  y: number,
): RaidUnit {
  const add = demoBossDef().phase2Add;
  const id = state.nextId++;
  noteEnemySpawn(state.spawnHeat, state.time, x, y);
  return {
    id,
    defId: "add",
    classId: "dps",
    team: 1,
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    hp: add.hp,
    maxHp: add.hp,
    damage: add.damage,
    radius: add.radius,
    speed: add.speed,
    attackRange: add.attackRange,
    attackCd: add.attackCd,
    attackCdLeft: 0.5,
    skillId: null,
    skillCdLeft: 0,
    skillCdMax: 0,
    attackAnimT: 0,
    stunnedUntil: 0,
    knock: null,
    aimX: x,
    aimY: y - 1,
    brain: freshBrain(),
    hit: createHitSignal(),
    barkCdLeft: 0,
    aggro: createRaidAggro(),
    telegraph: null,
    leap: null,
    brassCd: 0,
    leapCd: 0,
    chargeCd: 0,
    toughness: 0,
    toughnessMax: 0,
    breakStunDuration: 0,
    combatArmed: true,
    dropRemain: 0,
  };
}

/** Elite assault brute (~⅔ boss toughness cap 800); RaidUnit, not RaidBoss. */
export function makeBrute(
  state: RaidBattleState,
  x: number,
  y: number,
): RaidUnit {
  const script = assaultScript(state.encounter.scriptId);
  const stats = script.bruteStats;
  const tough = script.bruteToughness;
  const dropSec = Number(script.bruteDropSec) || 1.1;
  const id = state.nextId++;
  noteEnemySpawn(state.spawnHeat, state.time, x, y);
  return {
    id,
    defId: "brute",
    classId: "tank",
    team: 1,
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    hp: stats.hp,
    maxHp: stats.hp,
    damage: stats.damage,
    radius: stats.radius,
    speed: stats.speed,
    attackRange: stats.attackRange,
    attackCd: stats.attackCd,
    attackCdLeft: 0.4,
    skillId: null,
    skillCdLeft: 0,
    skillCdMax: 0,
    attackAnimT: 0,
    stunnedUntil: 0,
    knock: null,
    aimX: x,
    aimY: y - 1,
    brain: freshBrain(),
    hit: createHitSignal(),
    barkCdLeft: 0,
    aggro: createRaidAggro(),
    telegraph: null,
    leap: null,
    brassCd: 1.5,
    leapCd: 2.5,
    chargeCd: 3.0,
    toughness: tough.max,
    toughnessMax: tough.max,
    breakStunDuration: 0,
    combatArmed: false,
    dropRemain: dropSec,
  };
}

/**
 * Wave/boss charger — ~⅓ brute HP, no toughness bar, red line charge.
 * Stats from assault.json chargerStats / chargerSkills.
 */
export function makeCharger(
  state: RaidBattleState,
  x: number,
  y: number,
): RaidUnit {
  const script = assaultScript(state.encounter.scriptId);
  const stats = script.chargerStats;
  const dropSec = Number(script.chargerDropSec) || 0.55;
  const id = state.nextId++;
  noteEnemySpawn(state.spawnHeat, state.time, x, y);
  return {
    id,
    defId: "charger",
    classId: "dps",
    team: 1,
    x,
    y,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    hp: stats.hp,
    maxHp: stats.hp,
    damage: stats.damage,
    radius: stats.radius,
    speed: stats.speed,
    attackRange: stats.attackRange,
    attackCd: stats.attackCd,
    attackCdLeft: 0.35,
    skillId: null,
    skillCdLeft: 0,
    skillCdMax: 0,
    attackAnimT: 0,
    stunnedUntil: 0,
    knock: null,
    aimX: x,
    aimY: y - 1,
    brain: freshBrain(),
    hit: createHitSignal(),
    barkCdLeft: 0,
    aggro: createRaidAggro(),
    telegraph: null,
    leap: null,
    brassCd: 0,
    leapCd: 1.2,
    chargeCd: 0,
    toughness: 0,
    toughnessMax: 0,
    breakStunDuration: 0,
    combatArmed: false,
    dropRemain: dropSec,
  };
}

function demoBossDef() {
  const id = raidCatalog.bosses.demoBossId as keyof typeof raidCatalog.bosses.bosses;
  return raidCatalog.bosses.bosses[id];
}

function createBoss(): RaidBoss {
  const b = demoBossDef();
  const map = raidCatalog.balance.map;
  const spawn = clampToPlayBounds(map.bossX, map.bossY, b.radius);
  const boss: RaidBoss = {
    x: spawn.x,
    y: spawn.y,
    radius: b.radius,
    barsTotal: b.barsTotal,
    barIndex: 0,
    barHp: b.barMaxHp,
    barMaxHp: b.barMaxHp,
    phase: 1,
    phase2Triggered: false,
    atkCd: 2.5,
    brassCd: 5,
    leapCd: 7,
    periodicAddCdLeft: b.periodicAdd.cdSec,
    lateSummonWavesFired: b.lateSummon.waves.map(() => false),
    chargerSpawnFired: false,
    barkCdLeft: 0,
    meleeCd: 0.6,
    stunnedUntil: 0,
    breakStunDuration: 0,
    toughness: b.toughness.max,
    toughnessMax: b.toughness.max,
    telegraph: null,
    leap: null,
    damage: b.damage,
    attackRange: b.attackRange,
    speed: b.speed,
    chaseStopPad: b.chaseStopPad,
    vx: 0,
    vy: 0,
    intentVx: 0,
    intentVy: 0,
    attackAnimT: 0,
    meleeHitArmed: false,
    edgeDwellSec: 0,
    edgeRecenterCdLeft: 0,
    recenterSteerRemain: 0,
    aggro: createRaidAggro(),
    aggroTargetId: -1,
  };
  syncBossToughnessForPhase(boss);
  boss.toughness = boss.toughnessMax;
  return boss;
}

function createCommander(): RaidCommander {
  const manaTbl = (
    raidCatalog.balance as {
      mana?: { max?: number; start?: number };
    }
  ).mana;
  const manaMax = Math.max(1, Number(manaTbl?.max) || 100);
  const manaStart = Math.max(0, Math.min(manaMax, Number(manaTbl?.start) || manaMax));
  return {
    rallyCd: 0,
    focusCd: 0,
    callAddCd: 0,
    rallyUntil: 0,
    focusUntil: 0,
    regroupCd: 0,
    retreatCd: 0,
    holdCd: 0,
    damageMul: 1,
    order: createOrderSlot(),
    mana: manaStart,
    manaMax,
    lastActionKey: null,
    lastActionUntil: 0,
  };
}

export type CreateBattleStateOpts = {
  /** Injected [0,1) sim RNG — assault drip / spawn / CD jitter. */
  rng?: () => number;
  /** Injected [0,1) cosmetic bark RNG (E1 — never shared with sim). */
  cosmeticRng?: () => number;
};

export function createBattleState(
  partyIds: readonly string[],
  opts: CreateBattleStateOpts = {},
): RaidBattleState {
  const map = raidCatalog.balance.map;
  const state: RaidBattleState = {
    time: 0,
    phase: "playing",
    resultHoldRemain: 0,
    nextId: 1,
    units: [],
    boss: createBoss(),
    commander: createCommander(),
    encounter: {
      mode: "boss",
      scriptId: raidCatalog.assault.demoScriptId,
      waveIndex: 0,
      waveSpawned: false,
      toSpawn: 0,
      toSpawnSouth: 0,
      nextChunkIn: 0,
      bruteSpawnedForWave: false,
      chargerMidDripFiredMask: 0,
      chargerSpawnedForWave: false,
      delayRemain: 0,
      transitionRemain: 0,
      transitionPhase: null,
      bossCombatArmed: true,
      formationGoals: null,
      addBarkCdLeft: 0,
      // Boss-direct start: stage clock from t=0 (independent of assault waves).
      escalateAnchorTime: 0,
    },
    events: [],
    partyIds: [...partyIds],
    situations: {},
    spawnHeat: createSpawnHeat(),
    rng: opts.rng,
    cosmeticRng: opts.cosmeticRng,
  };
  const n = partyIds.length;
  const cols = Math.min(6, Math.max(3, Math.ceil(Math.sqrt(n))));
  const gap = raidCatalog.balance.crowd.sepRadius;
  partyIds.forEach((id, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const b = playBounds();
    const x =
      b.xMin +
      40 +
      (col / Math.max(1, cols - 1)) * Math.max(40, b.xMax - b.xMin - 80);
    const y = Math.min(map.allySpawnY + row * gap, b.yMax - 20);
    const pos = clampToPlayBounds(x, y);
    const u = makeAlly(state, id, pos.x, pos.y);
    state.units.push(u);
    if (u.classId === "tank") seedTankAggroBias(state.boss, u);
    push(state, {
      kind: RaidEvent.Spawn,
      unitId: u.id,
      x: u.x,
      y: u.y,
      team: 0,
    });
  });
  return state;
}

function push(state: RaidBattleState, ev: RaidSimEvent): void {
  state.events.push(ev);
}

export function drainEvents(state: RaidBattleState): RaidSimEvent[] {
  const out = state.events.slice();
  state.events.length = 0;
  return out;
}

/** Total boss HP fraction remaining (all bars). */
export function bossHpFraction(boss: RaidBoss): number {
  const done = boss.barIndex * boss.barMaxHp;
  const cur = Math.max(0, boss.barHp);
  const total = boss.barsTotal * boss.barMaxHp;
  return Math.max(0, (total - done - (boss.barMaxHp - cur)) / total);
}

export function barsRemaining(boss: RaidBoss): number {
  return Math.max(0, boss.barsTotal - boss.barIndex);
}

type ToughnessPhaseTable = {
  max: number;
  breakStunSec: number;
  phaseMul?: readonly number[];
  phaseMulCap?: number;
};

function toughnessPhaseTable(): ToughnessPhaseTable {
  return demoBossDef().toughness as ToughnessPhaseTable;
}

/**
 * Toughness phase from HP% bands.
 * Author `phaseMul` length = rising phases (default 3: ×1 / ×1.4 / ×1.8).
 * When `phaseMulCap` is set, one extra equal HP band uses the cap (phase 4+ → ×2).
 * Index: phase 0/1 → mul[0], phase 2 → mul[1], phase 3 → mul[2], beyond → cap.
 */
export function bossToughnessPhase(boss: RaidBoss): number {
  const t = toughnessPhaseTable();
  const muls = t.phaseMul ?? [1];
  const hasCap = t.phaseMulCap != null;
  const nBands = Math.max(1, muls.length + (hasCap ? 1 : 0));
  const frac = bossHpFraction(boss);
  const idx = Math.min(nBands - 1, Math.floor((1 - frac) * nBands));
  return idx + 1;
}

/** Phase → toughness multiplier (table-driven; cap after last authored phase). */
export function toughnessPhaseMul(phase: number): number {
  const t = toughnessPhaseTable();
  const muls = t.phaseMul ?? [1];
  const cap = t.phaseMulCap ?? muls[muls.length - 1] ?? 1;
  if (phase <= 1) return muls[0] ?? 1;
  const idx = phase - 1;
  if (idx < muls.length) return muls[idx] ?? 1;
  return cap;
}

/** Effective toughness max for current HP% phase (break threshold). */
export function bossToughnessMaxFor(boss: RaidBoss): number {
  const base = toughnessPhaseTable().max;
  return base * toughnessPhaseMul(bossToughnessPhase(boss));
}

/** Sync stored toughnessMax to current phase; preserve remaining fraction. */
export function syncBossToughnessForPhase(boss: RaidBoss): void {
  const nextMax = bossToughnessMaxFor(boss);
  if (nextMax === boss.toughnessMax) return;
  if (boss.toughnessMax > 0 && boss.toughness > 0) {
    boss.toughness = (boss.toughness / boss.toughnessMax) * nextMax;
  }
  boss.toughnessMax = nextMax;
}

export function damageBoss(
  state: RaidBattleState,
  amount: number,
  from?: RaidUnit,
  /** When set, Hit carries this for skill-specific presentation (e.g. marked_shot). */
  skillId?: string,
): void {
  if (state.phase !== "playing") return;
  // Entrance drop / assault / transition: unarmed Colossus is invulnerable
  // (mirror brute !combatArmed). Presentation may still show the falling body.
  if (isBossDormant(state)) return;
  // Dead heroes must not chip the boss (or trigger win).
  if (from && from.hp <= 0) return;
  const combat = raidCatalog.balance.combat;
  let left = amount;
  const boss = state.boss;
  if (state.time < boss.stunnedUntil) {
    left *= combat.breakHpDamageMul ?? 2;
  }
  while (left > 0 && boss.barIndex < boss.barsTotal) {
    const take = Math.min(boss.barHp, left);
    boss.barHp -= take;
    left -= take;
    if (boss.barHp <= 0) {
      boss.barIndex += 1;
      if (boss.barIndex >= boss.barsTotal) {
        boss.barHp = 0;
        boss.telegraph = null;
        boss.leap = null;
        boss.attackAnimT = 0;
        boss.meleeHitArmed = false;
        boss.intentVx = 0;
        boss.intentVy = 0;
        boss.vx = 0;
        boss.vy = 0;
        // Hold in bossDying so presentation can play death before win modal.
        state.phase = "bossDying";
        state.resultHoldRemain = raidDeathFx().bossDeathSec;
        if (from) {
          push(state, {
            kind: RaidEvent.Hit,
            x0: from.x,
            y0: from.y,
            x1: boss.x,
            y1: boss.y,
            damage: amount,
            attackerId: from.id,
            targetKind: "boss",
            ...(skillId ? { skillId } : {}),
          });
        }
        return;
      }
      boss.barHp = boss.barMaxHp;
    }
  }
  maybePhase2(state);
  syncBossToughnessForPhase(boss);
  if (from) {
    push(state, {
      kind: RaidEvent.Hit,
      x0: from.x,
      y0: from.y,
      x1: boss.x,
      y1: boss.y,
      damage: amount,
      attackerId: from.id,
      targetKind: "boss",
      ...(skillId ? { skillId } : {}),
    });
    noteDamageThreat(boss.aggro, from.id, amount, from.classId);
    noteDamageThreat(from.aggro, RAID_AGGRO_BOSS_ID, amount, from.classId);
    if (skillId === "marked_shot") {
      noteMarkedShotThreat(boss.aggro, from.id);
    }
    if (skillId === "shield_bash") {
      noteShieldBashTaunt(boss.aggro, from.id, state.time);
    }
    applyBossToughnessFromHit(state, from);
  }
}

/** Toughness chip from a successful ally hit on the boss. */
export function applyBossToughnessFromHit(
  state: RaidBattleState,
  from: RaidUnit,
): void {
  const combat = raidCatalog.balance.combat;
  const base =
    combat.toughnessDamage[from.classId] ?? combat.toughnessDamage.dps;
  const windowOpen =
    state.boss.telegraph != null || state.boss.leap?.phase === "windup";
  const mul =
    from.classId === "tank" && windowOpen ? combat.interruptWindowMul : 1;
  const chip = base * mul;
  applyBossToughness(state, chip);
  noteToughnessThreat(state.boss.aggro, from.id, chip);
}

export function applyBossToughness(
  state: RaidBattleState,
  amount: number,
): void {
  if (isBossDormant(state)) return;
  const boss = state.boss;
  if (state.time < boss.stunnedUntil) return;
  if (amount <= 0 || boss.toughness <= 0) return;
  boss.toughness = Math.max(0, boss.toughness - amount);
  if (boss.toughness <= 0) triggerBossBreak(state);
}

export function triggerBossBreak(state: RaidBattleState): void {
  const boss = state.boss;
  const stunSec = demoBossDef().toughness.breakStunSec;
  boss.toughness = 0;
  boss.telegraph = null;
  boss.leap = null;
  boss.intentVx = 0;
  boss.intentVy = 0;
  boss.vx = 0;
  boss.vy = 0;
  boss.breakStunDuration = stunSec;
  boss.stunnedUntil = state.time + stunSec;
  boss.attackAnimT = swingSecForBoss();
  clearSituation(state, RaidSituation.BossCastBrass);
  setSituation(state, RaidSituation.BossBreak, "raid.msg.break");
  push(state, { kind: RaidEvent.Break, x: boss.x, y: boss.y });
  tryEmitBossBark(state, "break");
}

function maybePhase2(state: RaidBattleState): void {
  const b = demoBossDef();
  if (state.boss.phase2Triggered) return;
  if (barsRemaining(state.boss) > b.phase2AtBarsRemaining) return;
  state.boss.phase2Triggered = true;
  state.boss.phase = 2;
  push(state, { kind: RaidEvent.Phase, phase: 2 });
  tryEmitBossBark(state, "phase");
  // Twin thrall pressure is periodicAdd (CD), not this bars gate.
}

/** South-facing ring points around the boss (playBounds-clamped). */
function ringSpawnPoints(
  state: RaidBattleState,
  count: number,
  padRadius = 0,
): { x: number; y: number }[] {
  const n = Math.max(0, Math.floor(count));
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const ang = -Math.PI / 2 + (i - (n - 1) / 2) * 0.45;
    const rawX = state.boss.x + Math.cos(ang) * 100;
    const rawY = state.boss.y + Math.sin(ang) * 100 + 40;
    out.push(clampToPlayBounds(rawX, rawY, padRadius));
  }
  return out;
}

/** Spawn N thralls in a south-facing ring around the boss (playBounds-clamped). */
export function spawnRingAdds(
  state: RaidBattleState,
  count: number,
): RaidUnit[] {
  const spawned: RaidUnit[] = [];
  for (const pos of ringSpawnPoints(state, count)) {
    const u = makeEnemyAdd(state, pos.x, pos.y);
    state.units.push(u);
    spawned.push(u);
    push(state, {
      kind: RaidEvent.Spawn,
      unitId: u.id,
      x: u.x,
      y: u.y,
      team: 1,
    });
  }
  pulseSpawnedAdds(state, spawned);
  return spawned;
}

/** Spawn N chargers in the same ring layout as thrall adds. */
export function spawnRingChargers(
  state: RaidBattleState,
  count: number,
): RaidUnit[] {
  const script = assaultScript(state.encounter.scriptId);
  const r = script.chargerStats?.radius ?? 18;
  const spawned: RaidUnit[] = [];
  for (const pos of ringSpawnPoints(state, count, r)) {
    const u = makeCharger(state, pos.x, pos.y);
    state.units.push(u);
    spawned.push(u);
    push(state, {
      kind: RaidEvent.Spawn,
      unitId: u.id,
      x: u.x,
      y: u.y,
      team: 1,
    });
  }
  return spawned;
}

function livingEnemyAdds(state: RaidBattleState): number {
  let n = 0;
  for (const u of state.units) {
    if (
      u.team === 1 &&
      u.hp > 0 &&
      enemyHasTag(u, RaidEnemyTag.PackChew)
    ) {
      n += 1;
    }
  }
  return n;
}

/** Tank-only peel pulse on fresh thralls so acquire beats mid-fight boss dump. */
function pulseSpawnedAdds(
  state: RaidBattleState,
  spawned: readonly RaidUnit[],
): void {
  const allies = state.units.filter((u) => u.team === 0);
  for (const add of spawned) {
    notePeelPulse(allies, add, state.time, { tanksOnly: true });
  }
}

/**
 * Periodic twin thralls (~every periodicAdd.cdSec) from fight start.
 * Caps living adds at periodicAdd.maxAdds; reuses phase2Add archetype + ring layout.
 */
function tryPeriodicAdd(state: RaidBattleState, dt: number): boolean {
  if (state.phase !== "playing") return false;
  const boss = state.boss;
  const tbl = demoBossDef().periodicAdd;
  boss.periodicAddCdLeft = Math.max(0, boss.periodicAddCdLeft - dt);
  if (boss.periodicAddCdLeft > 1e-6) return false;
  const living = livingEnemyAdds(state);
  const room = Math.max(0, tbl.maxAdds - living);
  if (room <= 0) {
    // Stay ready — retry when an add dies.
    boss.periodicAddCdLeft = 0;
    return false;
  }
  const count = Math.min(tbl.count, room);
  const spawned = spawnRingAdds(state, count);
  tryEmitAddWaveBark(state, spawned);
  boss.periodicAddCdLeft = tbl.cdSec;
  return true;
}

/** playBounds wall-edge points (same lateSummon xPad / yInset as thrall adds). */
function wallEdgeSpawnPoints(
  count: number,
  edge: "north" | "south",
  padRadius = 0,
): { x: number; y: number }[] {
  const b = playBounds();
  const late = demoBossDef().lateSummon;
  const n = Math.max(1, Math.floor(count));
  const xPad = late.xPad;
  const y =
    edge === "north" ? b.yMin + late.yInset : b.yMax - late.yInset;
  const span = Math.max(0, b.xMax - b.xMin - 2 * xPad);
  const out: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = n <= 1 ? 0.5 : i / (n - 1);
    const rawX = b.xMin + xPad + t * span;
    out.push(clampToPlayBounds(rawX, y, padRadius));
  }
  return out;
}

/** Spawn N thralls along a playBounds wall edge (table-driven pad/inset). */
function spawnWallEdgeAdds(
  state: RaidBattleState,
  count: number,
  edge: "north" | "south",
): RaidUnit[] {
  const spawned: RaidUnit[] = [];
  for (const pos of wallEdgeSpawnPoints(count, edge)) {
    const u = makeEnemyAdd(state, pos.x, pos.y);
    state.units.push(u);
    spawned.push(u);
    push(state, {
      kind: RaidEvent.Spawn,
      unitId: u.id,
      x: u.x,
      y: u.y,
      team: 1,
    });
  }
  pulseSpawnedAdds(state, spawned);
  return spawned;
}

/** Spawn N chargers along a playBounds wall edge (same band as thrall adds). */
function spawnWallEdgeChargers(
  state: RaidBattleState,
  count: number,
  edge: "north" | "south",
): RaidUnit[] {
  if (count <= 0) return [];
  const script = assaultScript(state.encounter.scriptId);
  const r = script.chargerStats?.radius ?? 18;
  const spawned: RaidUnit[] = [];
  for (const pos of wallEdgeSpawnPoints(count, edge, r)) {
    const u = makeCharger(state, pos.x, pos.y);
    state.units.push(u);
    spawned.push(u);
    push(state, {
      kind: RaidEvent.Spawn,
      unitId: u.id,
      x: u.x,
      y: u.y,
      team: 1,
    });
  }
  return spawned;
}

/** Spawn N thralls along the north playBounds edge (table-driven). */
export function spawnNorthWallAdds(
  state: RaidBattleState,
  count: number,
): RaidUnit[] {
  return spawnWallEdgeAdds(state, count, "north");
}

/** Spawn N thralls along the south playBounds edge (mirror of northWall). */
export function spawnSouthWallAdds(
  state: RaidBattleState,
  count: number,
): RaidUnit[] {
  return spawnWallEdgeAdds(state, count, "south");
}

/** Spawn N chargers along the north playBounds edge (same band as thralls). */
export function spawnNorthWallChargers(
  state: RaidBattleState,
  count: number,
): RaidUnit[] {
  return spawnWallEdgeChargers(state, count, "north");
}

/** Spawn N chargers along the south playBounds edge (same band as thralls). */
export function spawnSouthWallChargers(
  state: RaidBattleState,
  count: number,
): RaidUnit[] {
  return spawnWallEdgeChargers(state, count, "south");
}

/**
 * Late-fight charger pack: one-shot when barsRemaining ≤ atBarsRemaining.
 * Timed for mass-pressure feel alongside lateSummon thrall waves.
 */
function tryBossChargerSpawn(state: RaidBattleState): boolean {
  if (state.phase !== "playing") return false;
  if (isBossDormant(state)) return false;
  const boss = state.boss;
  if (boss.chargerSpawnFired) return false;
  const cfg = (
    demoBossDef() as {
      chargerSpawn?: { atBarsRemaining?: number; count?: number };
    }
  ).chargerSpawn;
  if (!cfg) return false;
  const gate = Math.max(0, Math.floor(Number(cfg.atBarsRemaining) || 0));
  const count = Math.max(0, Math.floor(Number(cfg.count) || 0));
  if (count <= 0) return false;
  if (barsRemaining(boss) > gate) return false;
  boss.chargerSpawnFired = true;
  // Same north-wall band as lateSummon thrall waves (boss-mode add region).
  spawnNorthWallChargers(state, count);
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "boss",
    wave: 0,
    bruteIndex: 0,
    messageKey: "raid.msg.bossCharger",
  });
  return true;
}

/**
 * Late-phase pattern: one-shot north-wall thrall waves gated by HP%.
 * Each `lateSummon.waves[i]` fires once when HP ≤ atHpFrac (waveFired).
 * No telegraph — Spawn events drive presentation.
 */
function tryLateSummon(state: RaidBattleState): boolean {
  if (state.phase !== "playing") return false;
  const boss = state.boss;
  if (boss.telegraph != null || boss.leap != null) return false;
  const late = demoBossDef().lateSummon;
  const hpFrac = bossHpFraction(boss);
  let fired = false;
  for (let i = 0; i < late.waves.length; i++) {
    if (boss.lateSummonWavesFired[i]) continue;
    const wave = late.waves[i];
    if (hpFrac > wave.atHpFrac) continue;
    const spawned = spawnNorthWallAdds(state, wave.count);
    tryEmitAddWaveBark(state, spawned);
    boss.lateSummonWavesFired[i] = true;
    fired = true;
  }
  return fired;
}

export function tryCommanderOrder(
  state: RaidBattleState,
  orderId: "rally" | "focus" | "callAdd" | "regroup" | "retreat" | "hold",
): boolean {
  if (state.phase !== "playing") return false;
  const cmd = state.commander;
  const table = raidCatalog.commander;
  if (orderId === "rally") {
    if (cmd.rallyCd > 0) return false;
    cmd.rallyCd = table.rally.cd;
    cmd.rallyUntil = state.time + table.rally.duration;
    cmd.damageMul = table.rally.damageMul;
    push(state, {
      kind: RaidEvent.Order,
      orderId: "rally",
      messageKey: table.rally.nameKey,
    });
    noteCommanderFeedback(state, table.rally.nameKey);
    return true;
  }
  if (orderId === "focus") {
    if (cmd.focusCd > 0) return false;
    cmd.focusCd = table.focus.cd;
    cmd.focusUntil = state.time + table.focus.duration;
    issueOrder(cmd.order, {
      kind: "focusTarget",
      issuedAt: state.time,
      until: cmd.focusUntil,
      priority: 1,
      targetId: null,
    });
    const focusTank = state.units.find(
      (u) => u.team === 0 && u.classId === "tank" && u.hp > 0,
    );
    if (focusTank) {
      noteFocusTaunt(
        state.boss.aggro,
        focusTank.id,
        state.time,
        cmd.focusUntil,
      );
    }
    push(state, {
      kind: RaidEvent.Order,
      orderId: "focus",
      messageKey: table.focus.nameKey,
    });
    noteCommanderFeedback(state, table.focus.nameKey);
    return true;
  }
  if (orderId === "callAdd") {
    if (cmd.callAddCd > 0) return false;
    cmd.callAddCd = table.callAdd.cd;
    const spawn = table.callAdd.spawn;
    const map = raidCatalog.balance.map;
    for (let i = 0; i < spawn.count; i++) {
      const rawX = map.width * 0.35 + i * 40;
      const rawY = map.allySpawnY + 20;
      const pos = clampToPlayBounds(rawX, rawY);
      const u = makeAlly(state, "dps_a", pos.x, pos.y, {
        hp: spawn.hpMul,
        damage: spawn.damageMul,
      });
      u.classId = spawn.classId as RaidClassId;
      state.units.push(u);
      push(state, {
        kind: RaidEvent.Spawn,
        unitId: u.id,
        x: u.x,
        y: u.y,
        team: 0,
      });
    }
    push(state, {
      kind: RaidEvent.Order,
      orderId: "callAdd",
      messageKey: table.callAdd.nameKey,
    });
    noteCommanderFeedback(state, table.callAdd.nameKey);
    return true;
  }
  // Tactic orders — short forced directives via the order channel.
  if (orderId === "regroup" || orderId === "retreat" || orderId === "hold") {
    const tbl = (table as unknown as Record<string, { cd: number; duration: number; nameKey: string }>)[orderId]!;
    const cdKey = (orderId + "Cd") as "regroupCd" | "retreatCd" | "holdCd";
    if (cmd[cdKey] > 0) return false;
    cmd[cdKey] = tbl.cd;
    const b = playBounds();
    const map = raidCatalog.balance.map;
    const point =
      orderId === "regroup"
        ? { x: (b.xMin + b.xMax) / 2, y: (b.yMin + b.yMax) / 2 + 90 }
        : orderId === "retreat"
          ? { x: map.width / 2, y: map.allySpawnY }
          : { x: 0, y: 0 }; // hold: per-unit stand ground
    issueOrder(cmd.order, {
      kind: orderId,
      issuedAt: state.time,
      until: state.time + tbl.duration,
      priority: orderId === "regroup" ? 1 : 2,
      x: point.x,
      y: point.y,
    });
    push(state, {
      kind: RaidEvent.Order,
      orderId,
      messageKey: tbl.nameKey,
    });
    noteCommanderFeedback(state, tbl.nameKey);
    return true;
  }
  return false;
}

type GestureManaCostId = "dash" | "groupDash" | "pin";

/** Sticky order expiry — dash/pin clear by arrive / interrupt / release, not a timer. */
const GESTURE_STICKY_UNTIL = 1e9;

function gestureTable() {
  return (
    raidCatalog.balance as {
      gesture?: {
        dashSpeedMul?: number;
        arriveDist?: number;
        groupOffsetScale?: number;
        groupOffsetCap?: number;
        commitEchoSec?: number;
        formationAnchorSec?: number;
        formationAnchorWeight?: number;
        manaCost?: Partial<Record<GestureManaCostId, number>>;
      };
      mana?: { regenPerSec?: number; max?: number };
    }
  );
}

function gestureManaCost(id: GestureManaCostId): number {
  const c = gestureTable().gesture?.manaCost?.[id];
  return Math.max(0, Number(c) || 0);
}

/** Soft formation TTL after dash/groupDash/pin (order clear does not clear anchor). */
function formationAnchorUntil(time: number): number {
  const sec = Number(gestureTable().gesture?.formationAnchorSec);
  const ttl = Number.isFinite(sec) && sec > 0 ? sec : 16;
  return time + ttl;
}

function setUnitGestureAnchor(
  u: RaidUnit,
  x: number,
  y: number,
  time: number,
): void {
  u.brain.gestureAnchor = { x, y, until: formationAnchorUntil(time) };
}

function trySpendMana(state: RaidBattleState, cost: number): boolean {
  if (cost <= 0) return true;
  if (state.commander.mana < cost) return false;
  state.commander.mana -= cost;
  return true;
}

function noteGestureAction(state: RaidBattleState, messageKey: string): void {
  noteCommanderFeedback(state, messageKey);
}

function pushUnitBark(
  state: RaidBattleState,
  u: RaidUnit,
  orderId: RaidBarkOrderId,
): void {
  const quirkId = raidHeroProfileFor(u.defId)?.quirkId ?? "default";
  const messageKey = pickOrderBarkKey(orderId, u.classId, quirkId, u.id);
  push(state, {
    kind: RaidEvent.Bark,
    unitId: u.id,
    x: u.x,
    y: u.y,
    messageKey,
  });
}

/**
 * Occasional boss bark — chance + cooldown (not every skill).
 * Pass `roll` for deterministic tests (else raidCosmeticRandom(state)).
 */
export function tryEmitBossBark(
  state: RaidBattleState,
  moment: RaidBossBarkMoment,
  roll?: number,
): boolean {
  const boss = state.boss;
  const r = roll ?? raidCosmeticRandom(state);
  if (
    !rollBarkGate(bossBarkChance(), boss.barkCdLeft, r)
  ) {
    return false;
  }
  const messageKey = pickBossBarkKey(
    moment,
    Math.floor(state.time * 1000) + moment.length,
  );
  push(state, {
    kind: RaidEvent.Bark,
    unitId: -1,
    x: boss.x,
    y: boss.y,
    messageKey,
    speaker: "boss",
  });
  boss.barkCdLeft = bossBarkCooldownSec();
  return true;
}

/** One thrall bark per large spawn batch (periodicAdd / lateSummon). */
export function tryEmitAddWaveBark(
  state: RaidBattleState,
  spawned: readonly RaidUnit[],
  roll?: number,
): boolean {
  const r = roll ?? raidCosmeticRandom(state);
  if (spawned.length < addWaveBarkMinCount()) return false;
  if (!rollBarkGate(addWaveBarkChance(), 0, r)) return false;
  const speaker = pickAddWaveSpeaker(
    spawned,
    Math.floor(state.time * 1000) + spawned.length,
  );
  if (!speaker) return false;
  push(state, {
    kind: RaidEvent.Bark,
    unitId: speaker.id,
    x: speaker.x,
    y: speaker.y,
    messageKey: pickAddWaveBarkKey(speaker.id),
  });
  return true;
}

/** One nearby living ally reacts to a party death (not everyone). */
export function tryEmitAllyDeathReact(
  state: RaidBattleState,
  dead: RaidUnit,
): boolean {
  if (dead.team !== 0) return false;
  const living = state.units.filter(
    (u) => u.team === 0 && u.hp > 0 && u.id !== dead.id,
  );
  const speaker = pickDeathReactSpeaker(living, dead.x, dead.y);
  if (!speaker) return false;
  const quirkId = raidHeroProfileFor(speaker.defId)?.quirkId ?? "default";
  push(state, {
    kind: RaidEvent.Bark,
    unitId: speaker.id,
    x: speaker.x,
    y: speaker.y,
    messageKey: pickDeathReactBarkKey(speaker.classId, quirkId, speaker.id),
  });
  return true;
}

/**
 * Ally big-hit bark — threshold + chance + per-unit cooldown.
 * Skip if already dead (death react covers that).
 */
export function tryEmitBigHitBark(
  state: RaidBattleState,
  u: RaidUnit,
  damage: number,
  roll?: number,
): boolean {
  const r = roll ?? raidCosmeticRandom(state);
  if (u.team !== 0 || u.hp <= 0 || damage <= 0) return false;
  if (damage < bigHitDamageThreshold(u.maxHp)) return false;
  if (!rollBarkGate(bigHitBarkChance(), u.barkCdLeft, r)) return false;
  const quirkId = raidHeroProfileFor(u.defId)?.quirkId ?? "default";
  push(state, {
    kind: RaidEvent.Bark,
    unitId: u.id,
    x: u.x,
    y: u.y,
    messageKey: pickBigHitBarkKey(u.classId, quirkId, u.id),
  });
  u.barkCdLeft = bigHitBarkCooldownSec();
  return true;
}

/** After ally HP loss — big-hit bark only if still alive. */
function afterAllyDamaged(
  state: RaidBattleState,
  u: RaidUnit,
  damage: number,
): void {
  if (u.hp > 0) tryEmitBigHitBark(state, u, damage);
}

/**
 * Unit swipe dash — clamps goal, spends mana, forces unit.dash until arrive.
 * Clears only on arrive (`arriveDist`) or interrupt (death / knock+stun / pin / new dash).
 * Soft-sep does not cancel.
 */
export function issueUnitDash(
  state: RaidBattleState,
  unitId: number,
  tx: number,
  ty: number,
): boolean {
  if (state.phase !== "playing") return false;
  const u = state.units.find((x) => x.id === unitId && x.team === 0 && x.hp > 0);
  if (!u) return false;
  const cost = gestureManaCost("dash");
  if (!trySpendMana(state, cost)) {
    noteGestureAction(state, "raid.gesture.noMana");
    return false;
  }
  const plan = planDashAroundHostile(u, tx, ty, state.boss);
  const goal = clampToPlayBounds(plan.finalX, plan.finalY, u.radius);
  const leg = clampToPlayBounds(plan.legX, plan.legY, u.radius);
  issueOrder(u.brain.order, {
    kind: "dash",
    issuedAt: state.time,
    until: state.time + GESTURE_STICKY_UNTIL,
    priority: 2,
    x: leg.x,
    y: leg.y,
  });
  setUnitGestureAnchor(u, goal.x, goal.y, state.time);
  invalidateUnitSelect(u);
  noteGestureAction(state, "raid.gesture.dash");
  push(state, {
    kind: RaidEvent.Order,
    orderId: "dash",
    messageKey: "raid.gesture.dash",
  });
  pushUnitBark(state, u, "dash");
  return true;
}

/**
 * Area-hold group dash — allies in radius dash to compressed offsets around (tx,ty).
 * Each unit clears independently on arrive; same interrupt list as unit dash.
 * Returns number of units issued (0 if none / mana fail).
 */
export function issueGroupDash(
  state: RaidBattleState,
  cx: number,
  cy: number,
  radius: number,
  tx: number,
  ty: number,
): number {
  if (state.phase !== "playing") return 0;
  const cost = gestureManaCost("groupDash");
  if (!trySpendMana(state, cost)) {
    noteGestureAction(state, "raid.gesture.noMana");
    return 0;
  }
  const g = gestureTable().gesture;
  const scale = Number(g?.groupOffsetScale) || 0.5;
  const cap = Number(g?.groupOffsetCap) || 40;
  const r2 = radius * radius;
  const movers: RaidUnit[] = [];
  for (const u of state.units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    const dx = u.x - cx;
    const dy = u.y - cy;
    if (dx * dx + dy * dy > r2) continue;
    let ox = dx * scale;
    let oy = dy * scale;
    const olen = Math.hypot(ox, oy);
    if (olen > cap && olen > 1e-6) {
      ox = (ox / olen) * cap;
      oy = (oy / olen) * cap;
    }
    const plan = planDashAroundHostile(u, tx + ox, ty + oy, state.boss);
    const goal = clampToPlayBounds(plan.finalX, plan.finalY, u.radius);
    const leg = clampToPlayBounds(plan.legX, plan.legY, u.radius);
    issueOrder(u.brain.order, {
      kind: "dash",
      issuedAt: state.time,
      until: state.time + GESTURE_STICKY_UNTIL,
      priority: 2,
      x: leg.x,
      y: leg.y,
    });
    setUnitGestureAnchor(u, goal.x, goal.y, state.time);
    invalidateUnitSelect(u);
    movers.push(u);
  }
  const n = movers.length;
  if (n > 0) {
    // One bubble only — pick a leader (prefer tank) to avoid N overlaps.
    // Formation anchors stay per-mover only (no party-wide pull).
    const speaker = pickGroupDashSpeaker(movers, tx, ty);
    if (speaker) pushUnitBark(state, speaker, "groupDash");
    noteGestureAction(state, "raid.gesture.groupDash");
    push(state, {
      kind: RaidEvent.Order,
      orderId: "groupDash",
      messageKey: "raid.gesture.groupDash",
    });
  }
  return n;
}

/** Hold-to-pin / release. Pin spends mana on enter. */
export function setUnitPin(
  state: RaidBattleState,
  unitId: number,
  pinned: boolean,
): boolean {
  if (state.phase !== "playing") return false;
  const u = state.units.find((x) => x.id === unitId && x.team === 0 && x.hp > 0);
  if (!u) return false;
  if (pinned) {
    const cost = gestureManaCost("pin");
    if (!trySpendMana(state, cost)) {
      noteGestureAction(state, "raid.gesture.noMana");
      return false;
    }
    issueOrder(u.brain.order, {
      kind: "pin",
      issuedAt: state.time,
      until: state.time + GESTURE_STICKY_UNTIL,
      priority: 2,
      x: u.x,
      y: u.y,
    });
    setUnitGestureAnchor(u, u.x, u.y, state.time);
    invalidateUnitSelect(u);
    noteGestureAction(state, "raid.gesture.pin");
    push(state, {
      kind: RaidEvent.Order,
      orderId: "pin",
      messageKey: "raid.gesture.pin",
    });
    pushUnitBark(state, u, "pin");
    return true;
  }
  clearUnitOrder(u);
  noteGestureAction(state, "raid.gesture.unpin");
  push(state, {
    kind: RaidEvent.Order,
    orderId: "unpin",
    messageKey: "raid.gesture.unpin",
  });
  return true;
}

function applySoftSep(state: RaidBattleState, dt: number): void {
  const crowd = raidCatalog.balance.crowd;
  const allies = state.units.filter((u) => u.team === 0 && u.hp > 0);
  const leap = state.boss.leap;
  const muteLeapInward =
    leap != null && skillZoneSoftSepInwardMute("leap");
  applySoftSepSpatial({
    items: allies,
    hash: sepHash,
    cellSize: crowd.cellSize,
    getId: (u) => u.id,
    getXY: (u) => ({ x: u.x, y: u.y }),
    queryRadius: () => crowd.sepRadius * 2,
    sepRadiusFor: () => crowd.sepRadius,
    sepStrength: crowd.sepStrength,
    sepTopK: crowd.sepTopK,
    sepMaxImpulse: crowd.sepMaxImpulse,
    walkEnter: crowd.walkEnter,
    getIntent: (u) => ({ intentVx: u.intentVx, intentVy: u.intentVy }),
    onCoincide: () => {},
    onIntegrate: (a, sx, sy) => {
      // Leap land disk: soft-sep must not shove rim-clear dodgers back in
      // (bond_cohesion multi-kill leak · leapD≈hitR). Outward sep kept.
      let ix = sx;
      let iy = sy;
      if (muteLeapInward && leap) {
        const muted = muteInwardLeapSepImpulse(
          a.x,
          a.y,
          sx,
          sy,
          leap.landX,
          leap.landY,
        );
        ix = muted.x;
        iy = muted.y;
      }
      const w = wallAwareSepImpulse(a.x, a.y, ix, iy, unitWallRect(a.radius));
      a.vx += w.x * dt;
      a.vy += w.y * dt;
    },
    scratchNear: sepNear,
  });
}

/**
 * Hard body sep after integrate — complements ally soft-sep.
 * Layering: soft-sep = ally↔ally velocity crowd; this = position clamp for
 * unit↔boss, party↔add, and enemy↔enemy via `bodySepPad` (+ reach min-sep
 * where hostileHardMinSep applies).
 * Skips knock flight. Skip unit↔boss while boss.leap is active — travel
 * would shove victims outside landRadius before resolveLeapLand.
 */
function applyHostileBodySep(state: RaidBattleState): void {
  const pad = bodySepPad();
  const boss = state.boss;
  // Dormant boss (assault/transition) is off-map — skip body sep against it.
  if (!boss.leap && !isBossDormant(state)) {
    const bossCoin = arenaCenterDirFrom(boss.x, boss.y);
    for (const u of state.units) {
      if (u.hp <= 0 || u.knock) continue;
      const minSep = hostileHardMinSep(u, boss);
      const out = separateFromFixed(
        { x: u.x, y: u.y, radius: u.radius },
        { x: boss.x, y: boss.y, radius: boss.radius },
        pad,
        bossCoin,
        minSep,
      );
      if (!out.separated) continue;
      const p = clampToPlayBounds(out.x, out.y, u.radius);
      u.x = p.x;
      u.y = p.y;
    }
  }

  const allies: RaidUnit[] = [];
  const enemies: RaidUnit[] = [];
  for (const u of state.units) {
    if (u.hp <= 0 || u.knock) continue;
    if (u.team === 0) allies.push(u);
    else enemies.push(u);
  }
  for (const a of allies) {
    for (const e of enemies) {
      // Same reach-capped hard min-sep as unit↔boss (ally is the mover).
      const minSep = hostileHardMinSep(a, e);
      const out = separateCircles(
        { x: a.x, y: a.y, radius: a.radius },
        { x: e.x, y: e.y, radius: e.radius },
        pad,
        undefined,
        minSep,
      );
      if (!out.separated) continue;
      const ap = clampToPlayBounds(out.ax, out.ay, a.radius);
      const ep = clampToPlayBounds(out.bx, out.by, e.radius);
      a.x = ap.x;
      a.y = ap.y;
      e.x = ep.x;
      e.y = ep.y;
    }
  }

  // Enemy↔enemy hard body sep — thralls/brutes must not share one point
  // (spawn/chase pile → burst damage). Soft-sep is ally-only; this is physics.
  for (let i = 0; i < enemies.length; i += 1) {
    for (let j = i + 1; j < enemies.length; j += 1) {
      const a = enemies[i]!;
      const b = enemies[j]!;
      const out = separateCircles(
        { x: a.x, y: a.y, radius: a.radius },
        { x: b.x, y: b.y, radius: b.radius },
        pad,
      );
      if (!out.separated) continue;
      const ap = clampToPlayBounds(out.ax, out.ay, a.radius);
      const bp = clampToPlayBounds(out.bx, out.by, b.radius);
      a.x = ap.x;
      a.y = ap.y;
      b.x = bp.x;
      b.y = bp.y;
    }
  }
}

function nearestAliveAlly(state: RaidBattleState): RaidUnit | null {
  const boss = state.boss;
  const pick = pickBossAggroTarget(
    boss,
    state.units,
    state.time,
    boss.aggroTargetId,
  );
  if (pick) boss.aggroTargetId = pick.id;
  return pick;
}

/** Allies within brass range (candidates for a meaningful telegraph). */
function alliesInBrassRange(
  state: RaidBattleState,
  range: number,
): RaidUnit[] {
  const boss = state.boss;
  const out: RaidUnit[] = [];
  for (const u of state.units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    if (dist(boss.x, boss.y, u.x, u.y) <= range + u.radius) out.push(u);
  }
  return out;
}

/**
 * Brass cone aim at cast start: primary aggro position from boss.
 * Fallback (no living target): last chase intent facing, else +Y (south).
 */
function brassAimAngle(boss: RaidBoss, primary: RaidUnit | null): number {
  if (primary && primary.hp > 0) {
    return Math.atan2(primary.y - boss.y, primary.x - boss.x);
  }
  const ilen = Math.hypot(boss.intentVx, boss.intentVy);
  if (ilen > 1e-3) return Math.atan2(boss.intentVy, boss.intentVx);
  return Math.PI / 2;
}

/**
 * Leap prefers high-threat backline (healer/dps bias on aggro score).
 * Returns null if only tanks (or no allies) — skip leap.
 */
function pickLeapTarget(state: RaidBattleState): RaidUnit | null {
  const pick = pickLeapAggroTarget(state.boss, state.units, state.time);
  if (pick) state.boss.aggroTargetId = pick.id;
  return pick;
}

function clampLandPoint(x: number, y: number): { x: number; y: number } {
  // Land with full body radius inset so feet stay inside playBounds.
  return clampToPlayBounds(x, y, demoBossDef().radius);
}

function clampBossToMap(boss: RaidBoss): void {
  // Pad = body radius: sim root ≈ feet/center — silhouette must stay inside.
  const p = clampToPlayBounds(boss.x, boss.y, boss.radius);
  boss.x = p.x;
  boss.y = p.y;
}

function resolveLeapLand(state: RaidBattleState): void {
  const boss = state.boss;
  const leap = boss.leap;
  if (!leap) return;
  const bdef = demoBossDef();
  const combat = raidCatalog.balance.combat;
  // Hit SSOT = telegraph land; clamp boss pose after so walls don't slide the disk.
  const landX = leap.landX;
  const landY = leap.landY;
  let hits = 0;
  const landDmg = scaleEnemyOutgoingDamage(state, leap.landDamage);
  for (const u of state.units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    if (dist(landX, landY, u.x, u.y) > leap.landRadius + u.radius) continue;
    u.hp -= landDmg;
    noteHit(u.hit, landDmg, BOSS_ATTACKER_ID, state.time);
    hits += 1;
    beginLeapKnockFlight(
      u,
      { ...boss, x: landX, y: landY },
      combat.leapKnockDist ?? combat.leapKnockback ?? 96,
      combat.leapFlightSec ?? 0.28,
      combat.leapKnockApex ?? 56,
      0,
    );
    // Leap hitstun covers flight + brief recover (not a long freeze).
    u.stunnedUntil = Math.max(
      u.stunnedUntil,
      state.time + (combat.leapLandStunSec ?? 1.65),
    );
    u.vx = 0;
    u.vy = 0;
    push(state, {
      kind: RaidEvent.Hit,
      x0: landX,
      y0: landY,
      x1: u.knock?.toX ?? u.x,
      y1: u.knock?.toY ?? u.y,
      damage: landDmg,
      targetKind: "unit",
      targetId: u.id,
    });
    if (u.hp <= 0) {
      finalizeUnitDeath(state, u, { keepKnock: true });
    } else {
      afterAllyDamaged(state, u, landDmg);
    }
  }
  push(state, {
    kind: RaidEvent.LeapLand,
    x: landX,
    y: landY,
    hits,
    danger: leap.danger,
    radius: leap.landRadius,
  });
  boss.x = landX;
  boss.y = landY;
  clampBossToMap(boss);
  boss.leap = null;
  boss.leapCd = bdef.leap.cooldownSec;
  boss.intentVx = 0;
  boss.intentVy = 0;
  boss.vx = 0;
  boss.vy = 0;
  if (leap.purpose === "recenter") {
    boss.edgeDwellSec = 0;
    const cfg = edgeRecenterCfg();
    if (cfg) boss.edgeRecenterCdLeft = Math.max(boss.edgeRecenterCdLeft, cfg.cooldownSec);
  }
}

function meleeSwingSec(): number {
  return swingSecForBoss();
}

function tryResolveMeleeHit(state: RaidBattleState): void {
  const boss = state.boss;
  if (!boss.meleeHitArmed || boss.attackAnimT <= 0) return;
  const melee = demoBossDef().melee;
  const swing = meleeSwingSec();
  const elapsed = swing - boss.attackAnimT;
  const hitAt = Math.max(0.05, Math.min(0.95, melee.hitAt ?? 0.45));
  if (elapsed < swing * hitAt) return;
  boss.meleeHitArmed = false;
  const reach = boss.radius + melee.reachPad;
  let hits = 0;
  const meleeDmg = scaleEnemyOutgoingDamage(state, melee.damage);
  for (const u of state.units) {
    if (u.team !== 0 || u.hp <= 0) continue;
    if (dist(boss.x, boss.y, u.x, u.y) > reach + u.radius) continue;
    hits += 1;
    u.hp -= meleeDmg;
    noteHit(u.hit, meleeDmg, BOSS_ATTACKER_ID, state.time);
    push(state, {
      kind: RaidEvent.Hit,
      x0: boss.x,
      y0: boss.y,
      x1: u.x,
      y1: u.y,
      damage: meleeDmg,
      targetKind: "unit",
      targetId: u.id,
    });
    if (u.hp <= 0) {
      finalizeUnitDeath(state, u);
    } else {
      afterAllyDamaged(state, u, meleeDmg);
    }
  }
  // Always emit reach slam VFX — miss still shows the AA footprint.
  push(state, {
    kind: RaidEvent.MeleeImpact,
    x: boss.x,
    y: boss.y,
    hits,
    radius: reach,
  });
}

function tickBoss(state: RaidBattleState, dt: number): void {
  const boss = state.boss;
  const bdef = demoBossDef();
  boss.attackAnimT = Math.max(0, boss.attackAnimT - dt);
  tryResolveMeleeHit(state);
  if (boss.attackAnimT <= 0) boss.meleeHitArmed = false;

  // Stun end → full toughness restore (phase-scaled max)
  if (boss.stunnedUntil > 0 && state.time >= boss.stunnedUntil) {
    boss.stunnedUntil = 0;
    boss.breakStunDuration = 0;
    syncBossToughnessForPhase(boss);
    boss.toughness = boss.toughnessMax;
    clearSituation(state, RaidSituation.BossBreak);
  }

  if (state.time < boss.stunnedUntil) {
    boss.telegraph = null;
    boss.leap = null;
    boss.intentVx = 0;
    boss.intentVy = 0;
    boss.vx = 0;
    boss.vy = 0;
    return;
  }

  boss.brassCd = Math.max(0, boss.brassCd - dt);
  boss.atkCd = Math.max(0, boss.atkCd - dt);
  boss.leapCd = Math.max(0, boss.leapCd - dt);
  boss.meleeCd = Math.max(0, boss.meleeCd - dt);
  boss.barkCdLeft = Math.max(0, boss.barkCdLeft - dt);
  boss.edgeRecenterCdLeft = Math.max(0, boss.edgeRecenterCdLeft - dt);

  // Edge dwell while grounded (leap travel relocates; telegraph hold still camps).
  if (!boss.leap) {
    updateBossEdgeDwell(state, dt);
  }

  // Melee AA roots briefly — skip chase/skills until swing ends
  if (boss.attackAnimT > 0) {
    boss.intentVx = 0;
    boss.intentVy = 0;
    boss.vx = 0;
    boss.vy = 0;
    return;
  }

  // 0) Late-phase north-wall thrall waves (HP% one-shot; does not root boss)
  tryLateSummon(state);

  // 1) Active leap — overrides brass/chase
  if (boss.leap) {
    const leap = boss.leap;
    leap.remain = Math.max(0, leap.remain - dt);
    boss.intentVx = 0;
    boss.intentVy = 0;
    boss.vx = 0;
    boss.vy = 0;

    if (leap.phase === "windup") {
      push(state, {
        kind: RaidEvent.LeapTelegraph,
        x: leap.landX,
        y: leap.landY,
        radius: leap.landRadius,
        remain: Math.max(0, leap.remain),
        duration: leap.duration,
        danger: leap.danger,
      });
      if (leap.remain <= 1e-6) {
        leap.phase = "travel";
        leap.remain = bdef.leap.travelSec;
        leap.duration = bdef.leap.travelSec;
        leap.fromX = boss.x;
        leap.fromY = boss.y;
      }
      return;
    }

    // travel: easeInOut toward land (non-uniform XY); walk via intent
    const travelDur = Math.max(0.05, leap.duration);
    const t = 1 - Math.max(0, leap.remain) / travelDur;
    const u = easeInOutCubic(Math.min(1, Math.max(0, t)));
    boss.x = leap.fromX + (leap.landX - leap.fromX) * u;
    boss.y = leap.fromY + (leap.landY - leap.fromY) * u;
    const dx = leap.landX - leap.fromX;
    const dy = leap.landY - leap.fromY;
    const len = Math.hypot(dx, dy) || 1;
    boss.intentVx = (dx / len) * boss.speed * 3;
    boss.intentVy = (dy / len) * boss.speed * 3;
    if (leap.remain <= 1e-6) {
      resolveLeapLand(state);
    }
    return;
  }

  // 2) Active telegraph (slam / brass) — hold position
  if (boss.telegraph) {
    boss.intentVx = 0;
    boss.intentVy = 0;
    boss.vx = 0;
    boss.vy = 0;
    boss.telegraph.remain -= dt;
    push(state, {
      kind: RaidEvent.Telegraph,
      shape: boss.telegraph.shape,
      x: boss.x,
      y: boss.y,
      range: boss.telegraph.range,
      coneDeg: boss.telegraph.coneDeg,
      aimAngle: boss.telegraph.aimAngle,
      remain: boss.telegraph.remain,
      duration: boss.telegraph.duration,
      danger: boss.telegraph.danger,
      skillId: boss.telegraph.skillId,
    });
    if (boss.telegraph.remain <= 0) {
      const skillId = boss.telegraph.skillId;
      const danger = boss.telegraph.danger;
      const range = boss.telegraph.range;
      const coneDeg = boss.telegraph.coneDeg;
      const aimAngle = boss.telegraph.aimAngle;
      let hits = 0;
      const tgDmg = scaleEnemyOutgoingDamage(state, boss.telegraph.damage);
      for (const u of state.units) {
        if (u.team !== 0 || u.hp <= 0) continue;
        if (!pointInTelegraph(u.x, u.y, boss)) continue;
        u.hp -= tgDmg;
        noteHit(u.hit, tgDmg, BOSS_ATTACKER_ID, state.time);
        hits += 1;
        const combat = raidCatalog.balance.combat;
        const aim = boss.telegraph.aimAngle;
        if (skillId === "slam") {
          const flightSec = combat.slamFlightSec ?? 0.14;
          beginKnockFlight(
            u,
            boss.x,
            boss.y,
            combat.slamKnockDist ?? 56,
            flightSec,
            combat.slamKnockApex ?? 28,
            {
              aimAngle: aim,
              aimBlend: combat.slamAimBlend ?? 0.15,
              recoverSec: combat.slamHitStunSec ?? 1.14,
            },
          );
        } else {
          const flightSec = combat.brassFlightSec ?? 0.11;
          beginKnockFlight(
            u,
            boss.x,
            boss.y,
            combat.brassKnockDist ?? 40,
            flightSec,
            combat.brassKnockApex ?? 16,
            {
              aimAngle: aim,
              aimBlend: combat.brassAimBlend ?? 0.45,
              recoverSec: combat.brassHitStunSec ?? 1.26,
            },
          );
        }
        push(state, {
          kind: RaidEvent.Hit,
          x0: boss.x,
          y0: boss.y,
          x1: u.knock?.toX ?? u.x,
          y1: u.knock?.toY ?? u.y,
          damage: tgDmg,
          targetKind: "unit",
          targetId: u.id,
        });
        if (u.hp <= 0) {
          // Slam/brass also beginKnockFlight — keep arc for death-after-flight.
          finalizeUnitDeath(state, u, { keepKnock: true });
        } else {
          afterAllyDamaged(state, u, tgDmg);
        }
      }
      push(state, {
        kind: RaidEvent.BrassResolve,
        x: boss.x,
        y: boss.y,
        hits,
        danger,
        skillId,
        range,
        coneDeg,
        aimAngle,
      });
      boss.telegraph = null;
      if (skillId === "brass") {
        boss.brassCd = bdef.brass.cooldownSec;
        clearSituation(state, RaidSituation.BossCastBrass);
      } else if (skillId === "slam") boss.atkCd = bdef.slam.cooldownSec;
    }
    return;
  }

  // 2b) Edge-camp recenter (leap to playBounds center, or soft-steer)
  if (tryBossEdgeRecenter(state, dt)) return;

  // 3) Start leap toward backline when ready
  if (boss.leapCd <= 0) {
    const target = pickLeapTarget(state);
    if (target) {
      beginBossLeap(state, target.x, target.y, "combat");
      return;
    }
  }

  const nearest = nearestAliveAlly(state);
  // Chase until melee reach — do NOT stop at attackRange (legacy, unused for chase).
  const stopDist = boss.radius + bdef.melee.reachPad;
  const meleeReach =
    boss.radius + bdef.melee.reachPad + (nearest?.radius ?? 0);

  // 4) Chase / orbit nearest ally
  if (nearest) {
    const d = dist(boss.x, boss.y, nearest.x, nearest.y);
    const dx = nearest.x - boss.x;
    const dy = nearest.y - boss.y;
    const len = Math.hypot(dx, dy) || 1;
    if (d > stopDist) {
      boss.intentVx = (dx / len) * boss.speed;
      boss.intentVy = (dy / len) * boss.speed;
    } else if (boss.meleeCd > 0 && boss.atkCd > 0) {
      // In melee band waiting on AA/slam CD — micro orbit so we don't idle-stand.
      const orbitMul = bdef.melee.orbitSpeedMul ?? 0.55;
      const ox = -dy / len;
      const oy = dx / len;
      const spd = boss.speed * orbitMul;
      boss.intentVx = ox * spd;
      boss.intentVy = oy * spd;
    } else {
      boss.intentVx = 0;
      boss.intentVy = 0;
    }
  } else {
    boss.intentVx = 0;
    boss.intentVy = 0;
  }
  boss.vx = boss.intentVx;
  boss.vy = boss.intentVy;

  const closePressure =
    nearest != null &&
    dist(boss.x, boss.y, nearest.x, nearest.y) <= meleeReach;

  // 5) Rare yellow slam AoE — only under close melee pressure
  if (closePressure && nearest && boss.atkCd <= 0) {
    const slam = bdef.slam;
    boss.telegraph = {
      shape: "circle",
      range: slam.radius,
      coneDeg: 360,
      aimAngle: Math.atan2(nearest.y - boss.y, nearest.x - boss.x),
      remain: slam.windupSec + slam.activeSec,
      duration: slam.windupSec + slam.activeSec,
      windupSec: slam.windupSec,
      damage: slam.damage,
      danger: raidDangerColor(slam.danger),
      skillId: "slam",
    };
    boss.intentVx = 0;
    boss.intentVy = 0;
    boss.vx = 0;
    boss.vy = 0;
    tryEmitBossBark(state, "telegraph");
    return;
  }

  // 6) Melee AA when in reach (short attack clip — not a telegraph skill)
  if (closePressure && nearest && boss.meleeCd <= 0) {
    const swing = meleeSwingSec();
    boss.attackAnimT = swing;
    boss.meleeHitArmed = true;
    boss.meleeCd = bdef.melee.cooldownSec;
    boss.intentVx = 0;
    boss.intentVy = 0;
    boss.vx = 0;
    boss.vy = 0;
    return;
  }

  // 7) Brass only under close pressure (not mid-range stand-and-cast)
  if (closePressure && nearest && boss.brassCd <= 0) {
    const brass = bdef.brass;
    const inRange = alliesInBrassRange(state, brass.range);
    if (inRange.length > 0) {
      // Aim at living primary aggro (same sticky pick as chase). Frozen for
      // windup readability — no mid-cast retarget. Fallback if gate changes:
      // last chase intent facing, else +Y (south).
      const aim = brassAimAngle(boss, nearest);
      boss.telegraph = {
        shape: "cone",
        range: brass.range,
        coneDeg: brass.coneDeg,
        aimAngle: aim,
        remain: brass.windupSec + brass.activeSec,
        duration: brass.windupSec + brass.activeSec,
        windupSec: brass.windupSec,
        damage: brass.damage,
        danger: raidDangerColor(brass.danger),
        skillId: "brass",
      };
      setSituation(state, RaidSituation.BossCastBrass, "raid.msg.brassCast");
      boss.intentVx = 0;
      boss.intentVy = 0;
      boss.vx = 0;
      boss.vy = 0;
      tryEmitBossBark(state, "telegraph");
      return;
    }
  }

  // 8) Integrate position
  boss.x += boss.vx * dt;
  boss.y += boss.vy * dt;
  clampBossToMap(boss);
}

/**
 * Tank shield bash / DPS marked shot when skill CD ready and in range.
 * Returns true if the active was spent (caller should skip AA this tick).
 */
function tryCastOffensiveActive(
  state: RaidBattleState,
  u: RaidUnit,
  intent: { attackTargetId: number | null },
  enemies: RaidUnit[],
  damageMul: number,
): boolean {
  const kit = raidActiveFor(u.classId);
  if (!kit || !(kit.damageMul != null && kit.damageMul > 0)) return false;
  const dmg = u.damage * damageMul * kit.damageMul;

  if (intent.attackTargetId != null) {
    const hitAdd = enemies.find(
      (e) => e.id === intent.attackTargetId && e.hp > 0,
    );
    if (
      hitAdd &&
      dist(u.x, u.y, hitAdd.x, hitAdd.y) < u.attackRange + hitAdd.radius
    ) {
      u.skillCdLeft = u.skillCdMax;
      u.attackAnimT = swingSecForClass(u.classId);
      if (isRaidBrute(hitAdd)) {
        damageBrute(state, hitAdd, dmg, u, kit.id);
        if (kit.toughnessMul != null && kit.toughnessMul > 1 && hitAdd.combatArmed) {
          const combat = raidCatalog.balance.combat;
          const base =
            combat.toughnessDamage[u.classId] ?? combat.toughnessDamage.dps;
          const windowOpen =
            hitAdd.telegraph != null || hitAdd.leap?.phase === "windup";
          const interruptMul =
            u.classId === "tank" && windowOpen ? combat.interruptWindowMul : 1;
          const extra = base * (kit.toughnessMul - 1) * interruptMul;
          if (extra > 0 && state.time >= hitAdd.stunnedUntil) {
            hitAdd.toughness = Math.max(0, hitAdd.toughness - extra);
            if (hitAdd.toughness <= 0) {
              triggerBruteBreak(state, hitAdd);
            }
          }
        }
      } else {
        hitAdd.hp -= dmg;
        noteHit(hitAdd.hit, dmg, u.id, state.time);
        noteDamageThreat(hitAdd.aggro, u.id, dmg, u.classId);
        noteDamageThreat(u.aggro, hitAdd.id, dmg, u.classId);
        push(state, {
          kind: RaidEvent.Hit,
          x0: u.x,
          y0: u.y,
          x1: hitAdd.x,
          y1: hitAdd.y,
          damage: dmg,
          attackerId: u.id,
          targetKind: "unit",
          targetId: hitAdd.id,
          skillId: kit.id,
        });
        if (hitAdd.hp <= 0) finalizeUnitDeath(state, hitAdd);
      }
      return true;
    }
    // Selected add out of melee — fall through to in-range boss opportunity.
  }

  // Unarmed Colossus (drop/hold/assault): no skill opportunity-fire.
  if (isBossDormant(state)) return false;
  const bossD = dist(u.x, u.y, state.boss.x, state.boss.y);
  if (
    state.phase !== "playing" ||
    bossD > state.boss.radius + u.attackRange
  ) {
    return false;
  }
  u.skillCdLeft = u.skillCdMax;
  u.attackAnimT = swingSecForClass(u.classId);
  damageBoss(state, dmg, u, kit.id);
  // Skill toughness bonus on top of the base chip from damageBoss.
  if (kit.toughnessMul != null && kit.toughnessMul > 1) {
    const combat = raidCatalog.balance.combat;
    const base =
      combat.toughnessDamage[u.classId] ?? combat.toughnessDamage.dps;
    const windowOpen =
      state.boss.telegraph != null || state.boss.leap?.phase === "windup";
    const interruptMul =
      u.classId === "tank" && windowOpen ? combat.interruptWindowMul : 1;
    applyBossToughness(
      state,
      base * (kit.toughnessMul - 1) * interruptMul,
    );
  }
  return true;
}

/**
 * Hitstun freezes allies except lethal escapes: DangerZone dodge, or survive
 * while thralls are inside AA (Soft≪AA — standing still is a wipe).
 * Engage thrall hitstun escape rejected (A 0.45→0.65).
 */
function allyEscapesHitstunMove(
  u: RaidUnit,
  enemies: readonly RaidUnit[],
): boolean {
  if (u.brain.select.activeId === "dodge") return true;
  if (u.brain.select.activeId !== "survive") return false;
  return !clearOfPackChewAttack(u.x, u.y, u.radius, enemies, 2);
}

function tickUnits(state: RaidBattleState, dt: number): void {
  const cmd = state.commander;
  if (state.time >= cmd.rallyUntil) cmd.damageMul = 1;
  const currentOrder = activeOrder(cmd.order, state.time);
  const focusActive = currentOrder?.kind === "focusTarget";
  const commanderForced =
    currentOrder && currentOrder.kind !== "focusTarget"
      ? `order.${currentOrder.kind}`
      : null;
  const focusTank =
    focusActive
      ? state.units.find((u) => u.team === 0 && u.classId === "tank" && u.hp > 0)
      : null;

  const allies = state.units.filter((u) => u.team === 0);
  // Unarmed (entrance-drop) brutes are not engage/AA targets.
  const enemies = state.units.filter(
    (u) =>
      u.team === 1 &&
      !(isRaidBrute(u) && !u.combatArmed) &&
      !(isRaidCharger(u) && !u.combatArmed),
  );
  const inRetreat =
    state.encounter.mode === "transition" &&
    state.encounter.transitionPhase === "retreat";

  for (const u of state.units) {
    if (u.hp <= 0) {
      u.vx = 0;
      u.vy = 0;
      u.intentVx = 0;
      u.intentVy = 0;
      u.attackAnimT = 0;
      u.attackCdLeft = 1e9;
      u.skillCdLeft = 1e9;
      continue;
    }
    u.attackCdLeft = Math.max(0, u.attackCdLeft - dt);
    u.skillCdLeft = Math.max(0, u.skillCdLeft - dt);
    u.attackAnimT = Math.max(0, u.attackAnimT - dt);
    u.barkCdLeft = Math.max(0, u.barkCdLeft - dt);

    if (u.knock) {
      u.vx = 0;
      u.vy = 0;
      u.intentVx = 0;
      u.intentVy = 0;
      // Flight advances in integrate pass below.
      continue;
    }

    if (state.time < u.stunnedUntil) {
      clearDashOrder(u);
      // Enemies stay frozen. Allies may still dodge DangerZones — hitstun must
      // not force standing in the next brass/leap after a chip.
      if (u.team !== 0) {
        u.vx = 0;
        u.vy = 0;
        u.intentVx = 0;
        u.intentVy = 0;
        continue;
      }
      // Drop fight commit so T0 dodge can win over sticky engage while stunned.
      u.brain.select.committedUntil = 0;
    }

    // Transition retreat: assault director owns ally motion.
    if (inRetreat && u.team === 0) {
      continue;
    }

    // Entrance-drop / casting brutes & chargers: skills own motion; skip thrall AA AI.
    if (u.team === 1 && isRaidBrute(u)) {
      if (!u.combatArmed || u.telegraph || u.leap) {
        continue;
      }
    }
    if (u.team === 1 && isRaidCharger(u)) {
      if (!u.combatArmed || u.telegraph || u.leap) {
        continue;
      }
    }

    if (u.team === 1) {
      // Add AI runs the same brain (sticky victim chase via ADD rules).
      const addIntent = computeAiIntent(
        u,
        state.boss,
        allies,
        enemies,
        null,
        state.time,
        null,
        null,
        !isBossDormant(state),
        state.spawnHeat,
      );
      u.aimX = addIntent.faceX;
      u.aimY = addIntent.faceY;
      const goal = clampToPlayBounds(addIntent.tx, addIntent.ty, u.radius);
      const dx = goal.x - u.x;
      const dy = goal.y - u.y;
      const len = Math.hypot(dx, dy) || 1;
      if (len > 6) {
        u.intentVx = (dx / len) * u.speed;
        u.intentVy = (dy / len) * u.speed;
      } else {
        u.intentVx = 0;
        u.intentVy = 0;
      }
      if (u.attackAnimT > 0) {
        u.intentVx = 0;
        u.intentVy = 0;
      }
      u.vx = u.intentVx;
      u.vy = u.intentVy;
      if (addIntent.wantAttack && addIntent.attackTargetId != null && u.attackCdLeft <= 0) {
        const victim = allies.find(
          (a) => a.id === addIntent.attackTargetId && a.hp > 0,
        );
        if (victim && dist(u.x, u.y, victim.x, victim.y) < u.attackRange + victim.radius) {
          const dmg = scaleEnemyOutgoingDamage(state, u.damage);
          victim.hp -= dmg;
          noteHit(victim.hit, dmg, u.id, state.time);
          noteDamageThreat(victim.aggro, u.id, dmg);
          noteDamageThreat(u.aggro, victim.id, dmg);
          notePeelPulse(
            state.units.filter((x) => x.team === 0),
            u,
            state.time,
          );
          u.attackCdLeft = u.attackCd;
          u.attackAnimT = swingSecForClass(u.classId);
          push(state, {
            kind: RaidEvent.Hit,
            x0: u.x,
            y0: u.y,
            x1: victim.x,
            y1: victim.y,
            damage: dmg,
            attackerId: u.id,
            targetKind: "unit",
            targetId: victim.id,
          });
          if (victim.hp <= 0) {
            finalizeUnitDeath(state, victim);
          } else {
            afterAllyDamaged(state, victim, dmg);
          }
        }
      }
      continue;
    }

    const unitOrd = activeOrder(u.brain.order, state.time);
    const forcedRuleId = unitOrd ? `unit.${unitOrd.kind}` : commanderForced;
    const intent = computeAiIntent(
      u,
      state.boss,
      allies,
      enemies,
      focusTank?.id ?? null,
      state.time,
      forcedRuleId,
      currentOrder,
      !isBossDormant(state),
      state.spawnHeat,
    );
    u.aimX = intent.faceX;
    u.aimY = intent.faceY;
    // Goals outside playBounds make units press the wall every frame (jitter).
    const goal = clampToPlayBounds(intent.tx, intent.ty, u.radius);
    const dx = goal.x - u.x;
    const dy = goal.y - u.y;
    const len = Math.hypot(dx, dy) || 1;
    const speed = u.speed * (intent.speedMul ?? 1);
    // Full intent speed always — crowd separation is soft-sep (lateral) + slots,
    // never forward damp (intentScale) which made overlapping allies crawl.
    // Soft-sep can park a middle melee unit just outside reach while gDist≤6 of
    // a ring goal; zeroing intent disables lateralize → permanent idle (no AA /
    // shield_bash). Keep closing while body is outside hostile reach and the
    // goal is still closer to that hostile.
    let needCloseOnReach = false;
    if (intent.wantHealId == null) {
      if (intent.attackTargetId != null) {
        const foe = enemies.find(
          (e) => e.id === intent.attackTargetId && e.hp > 0,
        );
        if (foe) {
          needCloseOnReach = shouldKeepClosingOnReach(
            u.x,
            u.y,
            goal.x,
            goal.y,
            foe.x,
            foe.y,
            u.attackRange + foe.radius,
          );
        }
      } else if (!isBossDormant(state)) {
        needCloseOnReach = shouldKeepClosingOnReach(
          u.x,
          u.y,
          goal.x,
          goal.y,
          state.boss.x,
          state.boss.y,
          state.boss.radius + u.attackRange,
        );
      }
    }
    const dodging = u.brain.select.activeId === "dodge";
    const surviving = u.brain.select.activeId === "survive";
    // Survive plant exempt only while thralls are chewing (Soft≪AA). Pure
    // brute fights keep low-measure plant so ironhorn sidestep is not cancelled.
    const surviveThrallKite =
      surviving &&
      !clearOfPackChewAttack(u.x, u.y, u.radius, enemies, 2);
    const escapeHitstun = dodging || surviveThrallKite;
    if (
      len > 6 ||
      needCloseOnReach ||
      ((dodging || surviveThrallKite) && len > 0.5)
    ) {
      const moveLen = Math.max(len, 1e-3);
      u.intentVx = (dx / moveLen) * speed;
      u.intentVy = (dy / moveLen) * speed;
    } else {
      u.intentVx = 0;
      u.intentVy = 0;
    }
    // Attack = stop at low measure (plant). High measure may kite-strafe in swing.
    // Dodge / thrall-survive must keep feet moving.
    const dashOrSprint =
      unitOrd?.kind === "dash" ||
      forcedRuleId === "unit.dash" ||
      forcedRuleId === "order.dash";
    if (
      u.attackAnimT > 0 &&
      !dashOrSprint &&
      !dodging &&
      !surviveThrallKite
    ) {
      const insight = insightAxisOf(resolveDisposition(u.defId, u.classId));
      if (plantBlendFromMeasure(insight) > SWING_STOP_PLANT_BLEND) {
        u.intentVx = 0;
        u.intentVy = 0;
      }
    }
    const stunned = state.time < u.stunnedUntil;
    // Hitstun: DangerZone dodge OR thrall-chewing survive may keep intent.
    if (stunned && !escapeHitstun) {
      u.intentVx = 0;
      u.intentVy = 0;
    }
    u.vx = u.intentVx;
    u.vy = u.intentVy;

    if (stunned) {
      // No heal / AA / actives while stunned — movement already gated above.
      continue;
    }

    if (intent.wantHealId != null && u.skillCdLeft <= 0) {
      const target = allies.find((a) => a.id === intent.wantHealId);
      if (target && target.hp > 0) {
        const kit = raidActiveFor("healer");
        const amount =
          kit?.amount ?? raidCatalog.balance.combat.healAmount;
        target.hp = Math.min(target.maxHp, target.hp + amount);
        u.skillCdLeft = u.skillCdMax > 0 ? u.skillCdMax : (kit?.cooldown ?? raidCatalog.balance.combat.healCd);
        u.attackAnimT = swingSecForClass(u.classId);
        push(state, {
          kind: RaidEvent.Heal,
          x: target.x,
          y: target.y,
          amount,
          targetId: target.id,
          healerId: u.id,
        });
      }
      continue;
    }

    // Class actives (tank shield bash / dps marked shot) — auto-cast when ready.
    if (
      intent.wantAttack &&
      u.skillCdLeft <= 0 &&
      u.skillCdMax > 0 &&
      (u.classId === "tank" || u.classId === "dps")
    ) {
      if (tryCastOffensiveActive(state, u, intent, enemies, cmd.damageMul)) {
        continue;
      }
    }

    if (intent.wantAttack && u.attackCdLeft <= 0) {
      const dmg = u.damage * cmd.damageMul;
      // Prefer AI-selected add when in melee; otherwise opportunity-fire boss
      // (selected target out of reach must not block in-range hostiles).
      if (intent.attackTargetId != null) {
        const hitAdd = enemies.find(
          (e) => e.id === intent.attackTargetId && e.hp > 0,
        );
        if (
          hitAdd &&
          dist(u.x, u.y, hitAdd.x, hitAdd.y) < u.attackRange + hitAdd.radius
        ) {
          u.attackCdLeft = u.attackCd;
          u.attackAnimT = swingSecForClass(u.classId);
          if (isRaidBrute(hitAdd)) {
            damageBrute(state, hitAdd, dmg, u);
          } else {
            hitAdd.hp -= dmg;
            noteHit(hitAdd.hit, dmg, u.id, state.time);
            noteDamageThreat(hitAdd.aggro, u.id, dmg, u.classId);
            noteDamageThreat(u.aggro, hitAdd.id, dmg, u.classId);
            push(state, {
              kind: RaidEvent.Hit,
              x0: u.x,
              y0: u.y,
              x1: hitAdd.x,
              y1: hitAdd.y,
              damage: dmg,
              attackerId: u.id,
              targetKind: "unit",
              targetId: hitAdd.id,
            });
            if (hitAdd.hp <= 0) {
              finalizeUnitDeath(state, hitAdd);
            }
          }
          continue;
        }
      }
      // Unarmed Colossus (drop/hold/assault): no AA opportunity-fire.
      if (isBossDormant(state)) continue;
      const bossD = dist(u.x, u.y, state.boss.x, state.boss.y);
      if (
        state.phase === "playing" &&
        bossD <= state.boss.radius + u.attackRange
      ) {
        u.attackCdLeft = u.attackCd;
        u.attackAnimT = swingSecForClass(u.classId);
        damageBoss(state, dmg, u);
      }
    }
  }

  applySoftSep(state, dt);

  for (const u of state.units) {
    // Dead units still advance corpse knock-flight (e.g. charger thrall fling).
    if (u.hp <= 0) {
      if (!u.knock) continue;
      const k = u.knock;
      k.t = Math.min(k.dur, k.t + dt);
      const s = sampleKnockFlight(
        k.t / k.dur,
        { x: k.fromX, y: k.fromY },
        { x: k.toX, y: k.toY },
        k.apex,
      );
      u.x = s.x;
      u.y = s.y;
      if (k.t >= k.dur - 1e-6) {
        u.x = k.toX;
        u.y = k.toY;
        u.knock = null;
      }
      const pDead = clampToPlayBounds(u.x, u.y, u.radius);
      u.x = pDead.x;
      u.y = pDead.y;
      u.vx = 0;
      u.vy = 0;
      u.intentVx = 0;
      u.intentVy = 0;
      continue;
    }
    // Knock: ignore soft-sep drift. Hitstun: hold unless dodging floors or
    // thrall-chewing survive (Soft≪AA — freeze would plant in pack).
    if (u.knock) {
      u.vx = 0;
      u.vy = 0;
      u.intentVx = 0;
      u.intentVy = 0;
    } else if (
      state.time < u.stunnedUntil &&
      !allyEscapesHitstunMove(u, enemies)
    ) {
      u.vx = 0;
      u.vy = 0;
      u.intentVx = 0;
      u.intentVy = 0;
    }
    if (u.knock) {
      const k = u.knock;
      k.t = Math.min(k.dur, k.t + dt);
      const s = sampleKnockFlight(
        k.t / k.dur,
        { x: k.fromX, y: k.fromY },
        { x: k.toX, y: k.toY },
        k.apex,
      );
      u.x = s.x;
      u.y = s.y;
      if (k.t >= k.dur - 1e-6) {
        u.x = k.toX;
        u.y = k.toY;
        const recover = k.recoverSec;
        u.knock = null;
        u.vx = 0;
        u.vy = 0;
        u.intentVx = 0;
        u.intentVy = 0;
        if (recover > 0) {
          u.stunnedUntil = Math.max(u.stunnedUntil, state.time + recover);
        }
      }
      const p = clampToPlayBounds(u.x, u.y, u.radius);
      u.x = p.x;
      u.y = p.y;
      zeroOutwardVelocity(u, unitWallRect(u.radius));
      continue;
    }
    if (
      state.time < u.stunnedUntil &&
      !allyEscapesHitstunMove(u, enemies)
    ) {
      // Hold position during recover hitstun (dodge / thrall-survive exception).
      continue;
    }
    u.x += u.vx * dt;
    u.y += u.vy * dt;
    const p = clampToPlayBounds(u.x, u.y, u.radius);
    u.x = p.x;
    u.y = p.y;
    zeroOutwardVelocity(u, unitWallRect(u.radius));
  }

  applyHostileBodySep(state);
}

export function tickRaid(state: RaidBattleState, dt: number): void {
  if (state.phase === "won" || state.phase === "lost") return;
  const t0 = raidFreezeNowMs();
  try {
    tickRaidBody(state, dt);
  } catch (err) {
    raidFreezeDiag.noteException("tickRaid", err);
    throw err;
  } finally {
    raidFreezeDiag.noteTickDuration("tickRaid", raidFreezeNowMs() - t0, {
      simTime: state.time,
      phase: state.phase,
      unitCount: state.units.length,
      chargerLeap: state.units.some(
        (u) => u.defId === RaidEnemyDefId.Charger && u.leap != null,
      ),
    });
  }
}

function tickRaidBody(state: RaidBattleState, dt: number): void {
  const clamped = Math.min(0.05, Math.max(0, dt));
  state.time += clamped;

  if (state.phase === "bossDying" || state.phase === "losing") {
    state.resultHoldRemain = Math.max(0, state.resultHoldRemain - clamped);
    if (state.resultHoldRemain <= 1e-6) {
      if (state.phase === "bossDying") {
        state.phase = "won";
        push(state, { kind: RaidEvent.Win });
      } else {
        state.phase = "lost";
        push(state, { kind: RaidEvent.Lose });
      }
      state.resultHoldRemain = 0;
    }
    return;
  }

  const cmd = state.commander;
  cmd.rallyCd = Math.max(0, cmd.rallyCd - clamped);
  cmd.focusCd = Math.max(0, cmd.focusCd - clamped);
  cmd.callAddCd = Math.max(0, cmd.callAddCd - clamped);
  cmd.regroupCd = Math.max(0, cmd.regroupCd - clamped);
  cmd.retreatCd = Math.max(0, cmd.retreatCd - clamped);
  cmd.holdCd = Math.max(0, cmd.holdCd - clamped);
  const regen = Number(gestureTable().mana?.regenPerSec) || 0;
  if (regen > 0) {
    cmd.mana = Math.min(cmd.manaMax, cmd.mana + regen * clamped);
  }

  decayRaidAggro(state.boss.aggro, clamped);
  for (const u of state.units) {
    if (u.hp > 0) decayRaidAggro(u.aggro, clamped);
  }

  if (isBossDormant(state)) {
    const fns: AssaultSpawnFns = {
      spawnNorthWallAdds,
      spawnSouthWallAdds,
      spawnRingAdds,
      spawnNorthWallChargers,
      spawnSouthWallChargers,
      spawnRingChargers,
      makeBrute,
      makeCharger,
      clampToPlayBounds,
      playBounds,
    };
    tickAssault(state, clamped, fns);
  } else {
    // Periodic twin adds — independent of boss cast/stun (unlike lateSummon north waves).
    tryPeriodicAdd(state, clamped);
    tryBossChargerSpawn(state);
    tickBoss(state, clamped);
  }
  tickBruteSkills(state, clamped);
  tickChargerSkills(state, clamped);
  tickUnits(state, clamped);
  // Skill land/brass/charge hits queued during skill tick — resolve after ally move
  // so T0 dodge travel counts on the paint-resolve frame (zones stay until flush).
  flushRaidDeferredSkillHits();
  syncSituations(state);

  const alive = state.units.some((u) => u.team === 0 && u.hp > 0);
  if (!alive && state.phase === "playing") {
    state.phase = "losing";
    state.resultHoldRemain = raidDeathFx().loseHoldSec;
  }
}

export type RaidHudPartyUnit = {
  id: number;
  defId: string;
  classId: RaidClassId;
  hpFrac: number;
  alive: boolean;
  skillId: string | null;
  skillNameKey: string | null;
  skillCdRemain: number;
  skillCdMax: number;
};

export type RaidHudEliteBar = {
  unitId: number;
  nameKey: string;
  /** Optional name format args (e.g. brute index). */
  nameN?: number;
  barsLabel: string;
  phaseLabel: string;
  hpFrac: number;
  /** Palette index for HP fill (boss bar index or lane). */
  barColorIndex: number;
  toughnessFrac: number;
  broken: boolean;
  /** `raid.hud.toughness` or `raid.hud.breakStun`. */
  toughLabelKey: string;
  /** Stage escalate stacks (>0 → passive icon). Shared stage clock. */
  escalateStacks: number;
};

export type RaidHudSnapshot = {
  phase: RaidBattleState["phase"];
  time: number;
  bossBarsTotal: number;
  bossBarIndex: number;
  bossBarHp: number;
  bossBarMaxHp: number;
  bossPhase: 1 | 2;
  toughnessFrac: number;
  bossBroken: boolean;
  allyAlive: number;
  allyTotal: number;
  /** Living+dead party frames for commander HP/skill CD strip. */
  party: readonly RaidHudPartyUnit[];
  /**
   * Top-N boss aggro ranks (debug/tests). Combat HUD does not render rank chips —
   * primary target uses `bossAggroTargetId` + world arrow.
   */
  bossThreat: readonly { unitId: number; threat: number; rank: number }[];
  /** Living primary aggro target id (-1 when none / empty table / boss dead). */
  bossAggroTargetId: number;
  /** Encounter director mode (assault → transition → boss). */
  encounterMode: RaidBattleState["encounter"]["mode"];
  /** 1-based wave for HUD chips; 0 when boss mode / not started. */
  encounterWave: number;
  encounterWaveTotal: number;
  /** Brutes spawned so far (1-based display n); 0 when none. */
  encounterBruteIndex: number;
  encounterBruteTotal: number;
  /** Boss multibar / aggro HUD visible (boss armed, or focus armed brute). */
  bossHudVisible: boolean;
  /** Name string key for the focus bar (colossus vs brute). */
  bossNameKey: string;
  /** True when HUD is showing focus brute(s), not the boss. */
  focusBrute: boolean;
  /**
   * Top HP lanes (1 boss, or up to 3 armed brutes). HUD renders these side by side.
   */
  eliteBars: readonly RaidHudEliteBar[];
  /** Stage-scoped enemy dmg escalate stacks (0 = hide passive). */
  escalateStacks: number;
  mana: number;
  manaMax: number;
  /** Last commander gesture/order messageKey (rules/tests). */
  lastActionKey: string | null;
  /**
   * Commit feedback key while `time < lastActionUntil`.
   * HUD prep chip: live prep > echo > idle. Never on enemy toast.
   */
  echoKey: string | null;
  /** Commander gesture prep chip (scene overlays live gesture; default idle). */
  prepKind:
    | "idle"
    | "holdUnit"
    | "holdEmpty"
    | "aimDash"
    | "pinCharged"
    | "aimGroupDash";
  /** Unit id under prep; -1 when none. */
  prepUnitId: number;
  rallyCd: number;
  focusCd: number;
  callAddCd: number;
  rallyReady: boolean;
  focusReady: boolean;
  callAddReady: boolean;
  regroupCd: number;
  retreatCd: number;
  holdCd: number;
  regroupReady: boolean;
  retreatReady: boolean;
  holdReady: boolean;
  /** Resolved ticker text key (priority over active situations). */
  toastKey: string | null;
  telegraph: {
    shape: "circle" | "cone" | "line";
    x: number;
    y: number;
    range: number;
    coneDeg: number;
    aimAngle: number;
    remain: number;
    duration: number;
  } | null;
};

const partySnapBuf: RaidHudPartyUnit[] = [];
const eliteBarsBuf: RaidHudEliteBar[] = [];

const MAX_ELITE_HUD_BARS = 3;

function toughnessFracFor(
  time: number,
  toughness: number,
  toughnessMax: number,
  stunnedUntil: number,
  breakStunDuration: number,
  fallbackBreakSec: number,
): { frac: number; broken: boolean } {
  const broken = time < stunnedUntil;
  const breakDur = Math.max(0.01, breakStunDuration || fallbackBreakSec);
  const frac = broken
    ? Math.max(0, Math.min(1, (stunnedUntil - time) / breakDur))
    : toughnessMax > 0
      ? toughness / toughnessMax
      : 0;
  return { frac, broken };
}

export function getHudSnapshot(state: RaidBattleState): RaidHudSnapshot {
  const allies = state.units.filter((u) => u.team === 0);
  const enc = state.encounter;
  const script = assaultScript(enc.scriptId);
  const armedBrutes = livingArmedBrutes(state)
    .slice()
    .sort((a, b) => a.id - b.id);
  const showBoss = enc.mode === "boss" && enc.bossCombatArmed;
  const showBrute = enc.mode === "assault" && armedBrutes.length > 0;
  const bossHudVisible = showBoss || showBrute;
  // Hide boss stack during transition/drop even if mode briefly overlaps.
  const hideDuringTransition = enc.mode === "transition";
  const hudVisible = bossHudVisible && !hideDuringTransition;

  const boss = state.boss;
  let bossBarsTotal = boss.barsTotal;
  let bossBarIndex = boss.barIndex;
  let bossBarHp = boss.barHp;
  let bossBarMaxHp = boss.barMaxHp;
  let bossPhase: 1 | 2 = boss.phase;
  let toughnessFrac = 0;
  let bossBroken = false;
  let bossNameKey = "raid.boss.colossus";
  let telegraph: RaidHudSnapshot["telegraph"] = null;
  let bossThreat: RaidHudSnapshot["bossThreat"] = [];
  let bossAggroTargetId = -1;
  eliteBarsBuf.length = 0;
  const escalateStacks = getEscalateStacks(state);

  if (showBrute) {
    const shown = armedBrutes.slice(0, MAX_ELITE_HUD_BARS);
    // Rank among all wave brutes (incl. dead) so HUD names stay sticky when one dies.
    const waveRank = bruteWaveRankById(state.units);
    for (let i = 0; i < shown.length; i++) {
      const u = shown[i]!;
      const t = toughnessFracFor(
        state.time,
        u.toughness,
        u.toughnessMax,
        u.stunnedUntil,
        u.breakStunDuration,
        script.bruteToughness.breakStunSec,
      );
      eliteBarsBuf.push({
        unitId: u.id,
        nameKey: bruteVariantNameKey(waveRank.get(u.id) ?? i),
        barsLabel: "",
        phaseLabel: "",
        hpFrac: u.maxHp > 0 ? Math.max(0, Math.min(1, u.hp / u.maxHp)) : 0,
        barColorIndex: i,
        toughnessFrac: t.frac,
        broken: t.broken,
        toughLabelKey: t.broken ? "raid.hud.breakStun" : "raid.hud.toughness",
        escalateStacks,
      });
    }
    // Legacy single-focus fields: lowest-HP among shown (focus target feel).
    const focusBruteUnit = shown.reduce((a, b) => (a.hp <= b.hp ? a : b));
    const focusLane =
      eliteBarsBuf.find((e) => e.unitId === focusBruteUnit.id) ??
      eliteBarsBuf[0]!;
    bossNameKey = focusLane.nameKey;
    bossBarsTotal = 1;
    bossBarIndex = 0;
    bossBarHp = focusBruteUnit.hp;
    bossBarMaxHp = focusBruteUnit.maxHp;
    bossPhase = 1;
    toughnessFrac = focusLane.toughnessFrac;
    bossBroken = focusLane.broken;
    const tg = focusBruteUnit.telegraph;
    telegraph = tg
      ? {
          shape: tg.shape,
          x: focusBruteUnit.x,
          y: focusBruteUnit.y,
          range: tg.range,
          coneDeg: tg.coneDeg,
          aimAngle: tg.aimAngle,
          remain: tg.remain,
          duration: tg.duration,
        }
      : null;
  } else if (showBoss) {
    const t = toughnessFracFor(
      state.time,
      boss.toughness,
      boss.toughnessMax,
      boss.stunnedUntil,
      boss.breakStunDuration,
      demoBossDef().toughness.breakStunSec,
    );
    toughnessFrac = t.frac;
    bossBroken = t.broken;
    bossThreat = bossThreatHudRanks(boss, allies, state.time);
    bossAggroTargetId =
      resolveBossAggroHudTarget(boss, allies, state.time)?.id ?? -1;
    const tg = boss.telegraph;
    telegraph = tg
      ? {
          shape: tg.shape,
          x: boss.x,
          y: boss.y,
          range: tg.range,
          coneDeg: tg.coneDeg,
          aimAngle: tg.aimAngle,
          remain: tg.remain,
          duration: tg.duration,
        }
      : null;
    const remain = boss.barsTotal - boss.barIndex;
    eliteBarsBuf.push({
      unitId: -1,
      nameKey: "raid.boss.colossus",
      barsLabel: `x${remain}`,
      phaseLabel:
        boss.phase === 2 ? "raid.hud.phase2" : "raid.hud.phase1",
      hpFrac:
        boss.barMaxHp > 0
          ? Math.max(0, Math.min(1, boss.barHp / boss.barMaxHp))
          : 0,
      barColorIndex: boss.barIndex,
      toughnessFrac: t.frac,
      broken: t.broken,
      toughLabelKey: t.broken ? "raid.hud.breakStun" : "raid.hud.toughness",
      escalateStacks,
    });
  }

  const toastKey = syncSituations(state);
  partySnapBuf.length = 0;
  for (const u of allies) {
    const kit = u.skillCdMax > 0 ? raidActiveFor(u.classId) : null;
    partySnapBuf.push({
      id: u.id,
      defId: u.defId,
      classId: u.classId,
      hpFrac: u.maxHp > 0 ? Math.max(0, u.hp / u.maxHp) : 0,
      alive: u.hp > 0,
      skillId: u.skillId,
      skillNameKey: kit?.nameKey ?? null,
      skillCdRemain: u.hp > 0 ? u.skillCdLeft : 0,
      skillCdMax: u.skillCdMax,
    });
  }
  const waveTotal = script.waves.length;
  const bruteTotal = script.brutes.length;
  let encounterWave = 0;
  let encounterBruteIndex = 0;
  if (enc.mode === "assault" || enc.mode === "transition") {
    encounterWave =
      enc.mode === "transition"
        ? waveTotal
        : enc.waveSpawned
          ? enc.waveIndex + 1
          : Math.max(0, enc.waveIndex);
    for (const b of script.brutes) {
      if (b.afterWave < enc.waveIndex) encounterBruteIndex++;
      else if (b.afterWave === enc.waveIndex && enc.bruteSpawnedForWave) {
        encounterBruteIndex++;
      }
    }
    if (enc.mode === "transition") encounterBruteIndex = bruteTotal;
  }
  return {
    phase: state.phase,
    time: state.time,
    bossBarsTotal,
    bossBarIndex,
    bossBarHp,
    bossBarMaxHp,
    bossPhase,
    toughnessFrac: hudVisible ? toughnessFrac : 0,
    bossBroken: hudVisible && bossBroken,
    allyAlive: allies.filter((u) => u.hp > 0).length,
    allyTotal: allies.length,
    party: partySnapBuf,
    bossThreat: hudVisible && showBoss ? bossThreat : [],
    bossAggroTargetId: hudVisible && showBoss ? bossAggroTargetId : -1,
    encounterMode: enc.mode,
    encounterWave,
    encounterWaveTotal: waveTotal,
    encounterBruteIndex,
    encounterBruteTotal: bruteTotal,
    bossHudVisible: hudVisible,
    bossNameKey,
    focusBrute: showBrute,
    eliteBars: hudVisible ? eliteBarsBuf.slice() : [],
    escalateStacks,
    mana: state.commander.mana,
    manaMax: state.commander.manaMax,
    lastActionKey: state.commander.lastActionKey,
    echoKey:
      state.commander.lastActionKey != null &&
      state.time < state.commander.lastActionUntil
        ? state.commander.lastActionKey
        : null,
    prepKind: "idle",
    prepUnitId: -1,
    rallyCd: state.commander.rallyCd,
    focusCd: state.commander.focusCd,
    callAddCd: state.commander.callAddCd,
    rallyReady: state.commander.rallyCd <= 0,
    focusReady: state.commander.focusCd <= 0,
    callAddReady: state.commander.callAddCd <= 0,
    regroupCd: state.commander.regroupCd,
    retreatCd: state.commander.retreatCd,
    holdCd: state.commander.holdCd,
    regroupReady: state.commander.regroupCd <= 0,
    retreatReady: state.commander.retreatCd <= 0,
    holdReady: state.commander.holdCd <= 0,
    toastKey,
    telegraph: hudVisible ? telegraph : null,
  };
}
